import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import {
    Clock,
    CheckCircle,
    Send,
    Wrench,
    CircleAlert,
    ArrowLeft,
    Eye,
    Upload,
    Download,
    X as XIcon,
    Plus,
    ClipboardList,
    UserRound,
    UserCheck,
    FileText,
    Layers,
    Camera,
    UploadCloud,
    ShieldCheck,
} from "lucide-react";
import { SafetyHeroBanner } from "../../../SafetyBannerArt";
import SafetyResponsiveSignatureCanvas from "../../../SafetyResponsiveSignatureCanvas";
import { getInternalOrganizationName } from "../../../shared/safetyHeaderUtils";

import {
    getSafetyChecklist,
    getSafetyTemplate,
    listSafetyCategories,
    listSafetyChecklists,
    listSafetyTemplates,
    listSafetyProjectReportConfigs,
    resolveActiveProjectId,
    resolveOrgId,
    submitSafetyChecklist,
    createAndSubmitSafetyChecklist,
    listContractorNamesByOrg,
    downloadSafetyReport
} from "../../../../../api";
import { showToast } from "../../../../../utils/toast";
import { getCurrentUserId } from "../../../../../utils/UserUtils";
import {
    getCurrentGPSLocation,
    fetchSiteGeoFence,
    evaluateLocationStatus,
    formatCoordinate,
    verifySitePresence,
} from "../../../../../services/geofenceService";

import SafetyChecklistHistoryModal from "../../SafetyChecklistHistoryModal";
import SafetyChecklistReadonlyModal from "../../SafetyChecklistReadonlyModal";

import SafetyChecklistSignOffSection from "../../SafetyChecklistSignOffSection";
import SafetyImageAnnotationModal from "../../SafetyImageAnnotationModal";
import SafetyCameraCaptureModal from "../../SafetyCameraCaptureModal";
import { DynamicSafetyInput } from "./SafetyChecklistCreateView";
import SafetyChecklistSectionHeader from "../../../shared/SafetyChecklistSectionHeader";
import { buildSectionedQuestionRows } from "../../../shared/safetySectionUtils";
import { getReportNumberPreview } from "../../../shared/safetyHeaderUtils";

// ── Dashboard scroll threshold ───────────────────────────────────────────────
const DASHBOARD_BUCKET_VISIBLE_ITEMS = 5;

/**
 * Wraps a bucket list with a vertical-scroll container only when the item
 * count exceeds DASHBOARD_BUCKET_VISIBLE_ITEMS.
 * Never introduces horizontal scrolling.
 */
const DashboardBucketList = ({ count, children, ariaLabel }) => {
    const shouldScroll = count > DASHBOARD_BUCKET_VISIBLE_ITEMS;
    return (
        <div
            className={[
                shouldScroll
                    ? "max-h-[380px] overflow-y-auto overflow-x-hidden overscroll-contain pr-1"
                    : "",
                "w-full",
            ]
                .filter(Boolean)
                .join(" ")}
            aria-label={ariaLabel}
        >
            {children}
        </div>
    );
};

const MAKER_QUEUE_STATUSES = [
    "Pending for Maker",
    "rejected_by_checker",
    "rejected_by_supervisor",
    "created",
];

function collectActiveMakerSubs(detail) {
    const out = [];
    (detail?.items || []).forEach((item) => {
        const sub = (item.submissions || []).find((s) =>
            MAKER_QUEUE_STATUSES.includes(s.status)
        );
        if (sub) out.push(sub);
    });
    return out;
}

function isRejectReworkStatus(st) {
    return st === "rejected_by_checker" || st === "rejected_by_supervisor";
}

/** First full forward submit (no reject rework): this request clears every maker-queue submission. */
function shouldShowMakerSignatureModal(detail, submissionsForApi, isBulkSubmit) {
    const makerSubs = collectActiveMakerSubs(detail);
    if (!makerSubs.length) return false;
    if (makerSubs.some((s) => isRejectReworkStatus(s.status))) return false;

    if (isBulkSubmit) return true;

    const ids = new Set(makerSubs.map((s) => s.id));
    const touched = new Set(
        (submissionsForApi || []).map((p) => p.submission_id).filter(Boolean)
    );
    if (ids.size !== touched.size) return false;
    for (const id of ids) {
        if (!touched.has(id)) return false;
    }
    return true;
}

// ─── Shared inline UI helpers ────────────────────────────────
const QuestionBadge = ({ number }) => (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-xs font-bold text-white shadow-sm">
        {number}
    </span>
);

const decodeJwtPayload = (token) => {
    try {
        if (!token || typeof token !== "string") return null;

        const parts = token.split(".");
        if (parts.length !== 3) return null;

        const base64Url = parts[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");

        const json = decodeURIComponent(
            atob(base64)
                .split("")
                .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
                .join("")
        );

        return JSON.parse(json);
    } catch {
        return null;
    }
};

const getCurrentUserProfile = () => {
    let fallbackProfile = null;

    // 1. First try known user object keys
    const objectKeys = [
        "USER_DATA",
        "user",
        "currentUser",
        "userData",
        "authUser",
    ];

    for (const key of objectKeys) {
        try {
            const raw = localStorage.getItem(key);

            if (!raw || raw === "undefined" || raw === "null") continue;

            const parsed = JSON.parse(raw);

            if (parsed?.user_type) {
                return parsed;
            }

            if (!fallbackProfile && (parsed?.user_id || parsed?.id)) {
                fallbackProfile = parsed;
            }
        } catch {
            // ignore and continue
        }
    }

    // 2. Try known token keys
    const tokenKeys = [
        "ACCESS_TOKEN",
        "access",
        "access_token",
        "accessToken",
        "token",
        "authToken",
    ];

    for (const key of tokenKeys) {
        const token = localStorage.getItem(key);
        const decoded = decodeJwtPayload(token);

        if (decoded?.user_type || decoded?.user_id) {
            return fallbackProfile ? { ...fallbackProfile, ...decoded } : decoded;
        }
    }

    // 3. Last fallback: scan localStorage for any JWT that contains user_type
    for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        const value = localStorage.getItem(key);

        const decoded = decodeJwtPayload(value);

        if (decoded?.user_type || decoded?.user_id) {
            return fallbackProfile ? { ...fallbackProfile, ...decoded } : decoded;
        }
    }

    return fallbackProfile || {};
};

export const autoResizeTextarea = (el) => {
    if (!el) return;

    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
};

const resolveMakerContext = () => {
    const profile = getCurrentUserProfile() || {};

    const accesses = Array.isArray(profile?.accesses) ? profile.accesses : [];

    const activeAccess =
        accesses.find((a) => a?.active && a?.project_id) ||
        accesses.find((a) => a?.project_id) ||
        null;

    const resolvedProjectId = String(
        resolveActiveProjectId?.() ||
        localStorage.getItem("ACTIVE_PROJECT_ID") ||
        localStorage.getItem("active_project_id") ||
        localStorage.getItem("PROJECT_ID") ||
        localStorage.getItem("project_id") ||
        activeAccess?.project_id ||
        profile?.project_id ||
        ""
    ).trim();

    const resolvedOrgId = String(
        resolveOrgId?.() ||
        localStorage.getItem("ORG_ID") ||
        localStorage.getItem("org_id") ||
        localStorage.getItem("organization_id") ||
        profile?.org ||
        profile?.org_id ||
        profile?.organization_id ||
        profile?.organization?.id ||
        ""
    ).trim();

    // Keep storage ready so existing api helpers also work.
    if (resolvedProjectId) {
        localStorage.setItem("ACTIVE_PROJECT_ID", resolvedProjectId);
        localStorage.setItem("PROJECT_ID", resolvedProjectId);
    }

    if (resolvedOrgId) {
        localStorage.setItem("ORG_ID", resolvedOrgId);
        localStorage.setItem("org_id", resolvedOrgId);
    }

    const resolvedProjectName = 
        activeAccess?.project_name || 
        activeAccess?.project?.name || 
        profile?.project_name || 
        profile?.project?.name || 
        "";

    return {
        projectId: resolvedProjectId,
        orgId: resolvedOrgId,
        projectName: resolvedProjectName,
        profile,
    };
};


export const resolveMediaUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http://") || path.startsWith("https://")) return path;

    const base =
        window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost"
            ? "http://127.0.0.1:8001"
            : "https://konstruct.world/checklists";

    const clean = path.startsWith("/") ? path : `/${path}`;
    return `${base}${clean}`;
};

const getInstructionLines = (text) => {
    return String(text || "")
        .split(/\r?\n/)
        .map((line) =>
            line
                .replace(/\t+/g, " ")
                .replace(/\s{2,}/g, " ")
                .trim()
        )
        .filter(Boolean);
};

const InstructionBlock = ({ image, text, className = "" }) => {
    const imageUrl = resolveMediaUrl(image);
    const lines = getInstructionLines(text);

    if (!imageUrl && !lines.length) {
        return null;
    }

    return (
        <div className={`rounded-2xl border border-border bg-card p-4 shadow-sm ${className}`}>
            <div className="mb-3">
                <h3 className="text-sm font-bold text-foreground">
                    Instructions
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                    Please read the instruction before checking the points.
                </p>
            </div>

            {imageUrl && (
                <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-muted/20 p-3 mb-3">
                    <img
                        src={imageUrl}
                        alt="Instruction"
                        className="max-h-[300px] w-full object-contain rounded-lg"
                    />
                </div>
            )}

            {lines.length > 0 && (
                <div className="overflow-hidden rounded-xl border border-border">
                    <table className="w-full border-collapse text-sm">
                        <tbody>
                            {lines.map((line, index) => (
                                <tr key={`${line}-${index}`}>
                                    {index === 0 && (
                                        <td
                                            rowSpan={lines.length}
                                            className="w-32 border border-border bg-muted/30 px-3 py-2 text-center text-xs font-bold uppercase tracking-wide text-muted-foreground align-middle"
                                        >
                                            Instructions
                                        </td>
                                    )}

                                    <td className="border border-border bg-white px-3 py-2 font-semibold text-foreground">
                                        {line}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};


const PhotoViewButton = ({ url, label = "View Photo" }) => {
    const finalUrl = resolveMediaUrl(url);
    if (!finalUrl) return null;

    return (
        <a
            href={finalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100 hover:border-blue-300"
        >
            <Eye className="h-3.5 w-3.5" />
            {label}
        </a>
    );
};




// const PhotoUploadArea = ({
//     id,
//     previewBase64,
//     onFileChange,
//     onRemove,
//     label = "Attach photo",
// }) => {
//     const inputRef = useRef(null);
//     const previewUrl = previewBase64
//         ? typeof previewBase64 === "string"
//             ? resolveMediaUrl(previewBase64)
//             : URL.createObjectURL(previewBase64)
//         : null;

//     const handleRemove = () => {
//         if (inputRef.current) {
//             inputRef.current.value = "";
//         }
//         onRemove?.();
//     };

//     return (
//         <div className="mt-3">
//             <p className="mb-1.5 text-xs font-medium text-muted-foreground">{label}</p>

//             {previewUrl ? (
//                 <div className="relative inline-block">
//                     <img
//                         src={previewUrl}
//                         alt="Preview"
//                         className="h-20 w-20 rounded-lg border border-border object-cover shadow-sm"
//                     />
//                     <button
//                         type="button"
//                         onClick={handleRemove}
//                         className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow transition-colors hover:bg-red-600"
//                         aria-label="Remove uploaded photo"
//                     >
//                         <XIcon className="h-3 w-3" />
//                     </button>
//                 </div>
//             ) : (
//                 <label
//                     htmlFor={`photo-upload-${id}`}
//                     className="flex cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:bg-muted/60"
//                 >
//                     <Upload className="h-4 w-4" />
//                     <span>Click to upload or take a photo</span>
//                 </label>
//             )}

//             <input
//                 ref={inputRef}
//                 id={`photo-upload-${id}`}
//                 type="file"
//                 accept="image/*"
//                 onChange={onFileChange}
//                 className="hidden"
//             />
//         </div>
//     );
// };



const PhotoUploadArea = ({
    id,
    previewBase64,
    onFileChange,
    onRemove,
    label = "Attach photo",
}) => {
    const galleryInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [annotationFile, setAnnotationFile] = useState(null);
    const [annotationOpen, setAnnotationOpen] = useState(false);

    const previewUrl = previewBase64
        ? typeof previewBase64 === "string"
            ? resolveMediaUrl(previewBase64)
            : URL.createObjectURL(previewBase64)
        : null;

    const handleInputChange = (event) => {
        const file = event.target.files?.[0];

        if (!file) return;

        if (!String(file.type || "").startsWith("image/")) {
            showToast("Please upload a valid image file.", "error");

            if (galleryInputRef.current) galleryInputRef.current.value = "";
            if (cameraInputRef.current) cameraInputRef.current.value = "";

            return;
        }

        setAnnotationFile(file);
        setAnnotationOpen(true);
    };

    const handleAnnotatedSave = (annotatedFile) => {
        setAnnotationOpen(false);
        setAnnotationFile(null);

        /*
            Keep existing parent usage working.
            Parent code already expects e.target.files[0].
        */
        onFileChange?.({
            target: {
                files: [annotatedFile],
            },
        });

        if (galleryInputRef.current) galleryInputRef.current.value = "";
        if (cameraInputRef.current) cameraInputRef.current.value = "";
    };

    const handleAnnotationClose = () => {
        setAnnotationOpen(false);
        setAnnotationFile(null);

        if (galleryInputRef.current) galleryInputRef.current.value = "";
        if (cameraInputRef.current) cameraInputRef.current.value = "";
    };

    const handleRemove = () => {
        if (galleryInputRef.current) galleryInputRef.current.value = "";
        if (cameraInputRef.current) cameraInputRef.current.value = "";

        setAnnotationOpen(false);
        setAnnotationFile(null);

        onRemove?.();
    };

    return (
        <div className="mt-3">
            {previewUrl ? (
                <div>
                    {label && (
                        <p className="mb-1.5 text-xs font-medium text-muted-foreground">
                            {label}
                        </p>
                    )}
                    <div className="relative inline-block">
                        <img
                            src={previewUrl}
                            alt="Preview"
                            className="h-20 w-20 rounded-lg border border-border object-cover shadow-sm"
                        />

                        <button
                            type="button"
                            onClick={handleRemove}
                            className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow transition-colors hover:bg-red-600"
                            aria-label="Remove uploaded photo"
                        >
                            <XIcon className="h-3 w-3" />
                        </button>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-orange-200 bg-orange-50/20 px-4 py-5 text-center transition-colors hover:bg-orange-50/40">
                    <UploadCloud className="mb-2 h-7 w-7 text-orange-400" />
                    <p className="mb-1 text-xs sm:text-sm font-semibold text-slate-700">
                        {label || "Attach photo"}
                    </p>
                    <p className="mb-3 text-[11px] text-slate-400">PNG, JPG, JPEG</p>
                    <div className="flex flex-wrap items-center justify-center gap-2.5">
                        <label
                            htmlFor={`photo-upload-gallery-${id}`}
                            className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3.5 py-2 text-xs font-semibold text-orange-700 hover:bg-orange-100 transition-colors shadow-sm"
                        >
                            <UploadCloud className="h-4 w-4 text-orange-600" />
                            <span>Select from Gallery</span>
                        </label>

                        <button
                            type="button"
                            onClick={() => setCameraOpen(true)}
                            className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-orange-600 px-3.5 py-2 text-xs font-semibold text-white hover:bg-orange-700 transition-colors shadow-sm"
                        >
                            <Camera className="h-4 w-4" />
                            <span>Take Photo</span>
                        </button>
                    </div>
                </div>
            )}

            <input
                ref={galleryInputRef}
                id={`photo-upload-gallery-${id}`}
                type="file"
                accept="image/*"
                onChange={handleInputChange}
                className="hidden"
            />

            <input
                ref={cameraInputRef}
                id={`photo-upload-camera-${id}`}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleInputChange}
                className="hidden"
            />

            <SafetyCameraCaptureModal
                open={cameraOpen}
                onClose={() => setCameraOpen(false)}
                onCapture={(capturedFile) => {
                    if (!capturedFile) return;
                    setAnnotationFile(capturedFile);
                    setAnnotationOpen(true);
                }}
                title="Take Photo"
            />

            <SafetyImageAnnotationModal
                open={annotationOpen}
                file={annotationFile}
                onClose={handleAnnotationClose}
                onSave={handleAnnotatedSave}
            />
        </div>
    );
};


const getOptionToneByIndex = (index) => {
    if (index === 0) {
        return {
            active: "border-green-500 bg-green-500 text-white shadow-sm",
            idle: "border-green-200 bg-green-50 text-green-700 hover:bg-green-100",
        };
    }

    if (index === 1) {
        return {
            active: "border-red-500 bg-red-500 text-white shadow-sm",
            idle: "border-red-200 bg-red-50 text-red-700 hover:bg-red-100",
        };
    }

    if (index === 2) {
        return {
            active: "border-yellow-500 bg-yellow-500 text-white shadow-sm",
            idle: "border-yellow-200 bg-yellow-50 text-yellow-700 hover:bg-yellow-100",
        };
    }

    return {
        active: "border-slate-500 bg-slate-500 text-white shadow-sm",
        idle: "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100",
    };
};

const TemplateOptionButtons = ({ options = [], value, onChange }) => {
    const normalizedOptions =
        Array.isArray(options) && options.length
            ? options
            : ["Yes", "No", "N/A"];

    return (
        <div className="flex flex-wrap gap-2">
            {normalizedOptions.map((opt, index) => {
                const label =
                    typeof opt === "string"
                        ? opt
                        : opt?.label || opt?.name || opt?.title || "";

                if (!label) return null;

                const active =
                    String(value || "").trim().toLowerCase() ===
                    String(label || "").trim().toLowerCase();

                const toneClasses = getOptionToneByIndex(index);

                return (
                    <button
                        key={`${label}-${index}`}
                        type="button"
                        onClick={() => onChange(label)}
                        className={`rounded-lg border px-4 py-2 text-sm font-semibold transition ${
                            active ? toneClasses.active : toneClasses.idle
                        }`}
                    >
                        {label}
                    </button>
                );
            })}
        </div>
    );
};



const RemarksBubble = ({ label, text }) => (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        <span className="font-semibold">{label}:</span> {text}
    </div>
);


// FILTER HELPERS

const ChecklistFilters = ({
    filters,
    setFilters,
    contractorOptions,
    typeOptions,
}) => {
    const updateFilter = (key, value) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const clearFilters = () => {
        setFilters({
            fromDate: "",
            toDate: "",
            contractor: "all",
            status: "all",
            type: "all",
        });
    };

    return (
        <div className="mb-6 rounded-xl border bg-card p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-foreground">
                    Filters
                </h3>

                <button
                    type="button"
                    onClick={clearFilters}
                    className="rounded-lg border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted"
                >
                    Clear
                </button>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <div>
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">
                        From Date
                    </label>
                    <input
                        type="date"
                        value={filters.fromDate}
                        onChange={(e) => updateFilter("fromDate", e.target.value)}
                        className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                    />
                </div>

                <div>
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">
                        To Date
                    </label>
                    <input
                        type="date"
                        value={filters.toDate}
                        onChange={(e) => updateFilter("toDate", e.target.value)}
                        className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                    />
                </div>

                <div>
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">
                        Contractor
                    </label>
                    <select
                        value={filters.contractor}
                        onChange={(e) => updateFilter("contractor", e.target.value)}
                        className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                    >
                        <option value="all">All Contractors</option>
                        {contractorOptions.map((name) => (
                            <option key={name} value={name}>
                                {name}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">
                        Status
                    </label>
                    <select
                        value={filters.status}
                        onChange={(e) => updateFilter("status", e.target.value)}
                        className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                    >
                        <option value="all">All</option>
                        <option value="rejected">Rejected</option>
                        <option value="approved">Approved</option>
                    </select>
                </div>

                <div>
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">
                        Type
                    </label>
                    <select
                        value={filters.type}
                        onChange={(e) => updateFilter("type", e.target.value)}
                        className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                    >
                        <option value="all">All Types</option>
                        {typeOptions.map((title) => (
                            <option key={title} value={title}>
                                {title}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
};

const normalizeFilterValue = (value) =>
    String(value || "").trim().toLowerCase();

const getChecklistCreatedDate = (item) =>
    item?.created_at ? String(item.created_at).slice(0, 10) : "";

const getChecklistContractor = (item) =>
    String(
        item?.contractor_name ||
        item?.name_of_contractor ||
        item?.safety_report_meta?.name_of_contractor ||
        ""
    ).trim();

const getChecklistInspectionReportNo = (item) =>
    String(
        item?.inspection_report_no ||
        item?.safety_report_no ||
        item?.report_header_meta?.inspection_report_no ||
        item?.safety_report_meta?.inspection_report_no ||
        item?.report_meta?.inspection_report_no ||
        ""
    ).trim();

const getChecklistItemInstanceCount = (item) => {
    const raw =
        item?.item_instance_count ??
        item?.multi_item_count ??
        item?.item_instances?.length ??
        1;
    const count = Number(raw);
    return Number.isFinite(count) && count > 0 ? count : 1;
};

const MetaChip = ({ icon, label, value, tone = "slate" }) => {
    const toneClasses = {
        slate:  "bg-slate-50 border-slate-200 text-slate-700",
        blue:   "bg-blue-50 border-blue-200 text-blue-700",
        purple: "bg-purple-50 border-purple-200 text-purple-700",
    }[tone];

    return (
        <span
            className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${toneClasses}`}
        >
            {icon}
            {label && <span className="text-muted-foreground/80">{label}</span>}
            <span className="truncate font-semibold">{value}</span>
        </span>
    );
};

const ChecklistListMeta = ({ item }) => {
    const contractor = getChecklistContractor(item);
    const reportNo = getChecklistInspectionReportNo(item);
    const itemCount = getChecklistItemInstanceCount(item);
    const isInternal =
        !contractor ||
        contractor === "—" ||
        contractor === "-" ||
        contractor.toLowerCase() === "internal" ||
        contractor.toLowerCase() === "internal flow";
    const orgName = getInternalOrganizationName(item);

    return (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <MetaChip
                icon={<UserRound className="h-3 w-3 shrink-0" />}
                label={isInternal ? null : "Contractor:"}
                value={isInternal ? orgName : contractor}
                tone="blue"
            />

            <MetaChip
                icon={<FileText className="h-3 w-3 shrink-0" />}
                label="Report No.:"
                value={reportNo || "—"}
                tone="slate"
            />

            {itemCount > 1 && (
                <MetaChip
                    icon={<Layers className="h-3 w-3 shrink-0" />}
                    label="Equipment:"
                    value={itemCount}
                    tone="purple"
                />
            )}

            {item?.currently_with && item.currently_with !== "Not Available" && (
                <MetaChip
                    icon={<UserCheck className="h-3 w-3 shrink-0" />}
                    label="Currently With:"
                    value={item.currently_with}
                    tone="amber"
                />
            )}
        </div>
    );
};

const getChecklistType = (item) =>
    String(item?.template_title || item?.name || "").trim();

const isApprovedChecklist = (item) =>
    String(item?.status || "").toLowerCase() === "completed" ||
    String(item?.review_state?.closed_reason || "").toLowerCase() === "approved";

const isRejectedChecklist = (item) =>
    String(item?.status || "").toLowerCase() === "closed_rejected" ||
    Number(item?.maker_rejected_points || 0) > 0;

const applyChecklistFilters = (list, filters) => {
    return (list || []).filter((item) => {
        const createdDate = getChecklistCreatedDate(item);

        if (filters.fromDate && (!createdDate || createdDate < filters.fromDate)) {
            return false;
        }

        if (filters.toDate && (!createdDate || createdDate > filters.toDate)) {
            return false;
        }

        if (
            filters.contractor !== "all" &&
            normalizeFilterValue(getChecklistContractor(item)) !==
            normalizeFilterValue(filters.contractor)
        ) {
            return false;
        }

        if (
            filters.type !== "all" &&
            normalizeFilterValue(getChecklistType(item)) !==
            normalizeFilterValue(filters.type)
        ) {
            return false;
        }

        if (filters.status === "approved" && !isApprovedChecklist(item)) {
            return false;
        }

        if (filters.status === "rejected" && !isRejectedChecklist(item)) {
            return false;
        }

        return true;
    });
};


// const ReportHeaderInfoCard = ({ detail }) => {
//     const meta =
//         detail?.report_header_meta ||
//         detail?.safety_report_meta ||
//         detail?.report_meta ||
//         {};

//     const rows = [
//         ["Format No.", meta.format_no],
//         ["Revision No.", meta.revision_no],
//         ["Issued Date", meta.issued_date],
//         ["Revision Date", meta.revision_date],
//         ["Project", meta.project],
//         ["Inspection Report No.", meta.inspection_report_no],
//         ["Name of Contractor", meta.name_of_contractor],
//         ["Date of Inspection", meta.date_of_inspection],
//         ["Make / Model", meta.make_model],
//         ["Identification No.", meta.identification_no],
//         ["Location", meta.location],
//         ["Name of Operator", meta.name_of_operator],
//     ];

//     return (
//         <div className="mb-6 rounded-xl border bg-card p-5 shadow-sm">
//             <h2 className="mb-4 text-sm font-bold text-foreground">
//                 Checklist Details
//             </h2>

//             <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
//                 {rows.map(([label, value]) => (
//                     <div
//                         key={label}
//                         className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2"
//                     >
//                         <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
//                             {label}
//                         </p>
//                         <p className="mt-1 break-words text-sm font-semibold text-foreground">
//                             {String(value || "").trim() || "—"}
//                         </p>
//                     </div>
//                 ))}
//             </div>
//         </div>
//     );
// };


// const formatTodayDDMMYYYY = () => {
//     const now = new Date();
//     const dd = String(now.getDate()).padStart(2, "0");
//     const mm = String(now.getMonth() + 1).padStart(2, "0");
//     const yyyy = now.getFullYear();

//     return `${dd}-${mm}-${yyyy}`;
// };

// const ReadOnlyHeaderField = ({ label, value }) => (
//     <div className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2">
//         <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
//             {label}
//         </p>
//         <p className="mt-1 break-words text-sm font-semibold text-foreground">
//             {String(value || "").trim() || "—"}
//         </p>
//     </div>
// );

// const EditableHeaderInput = ({ label, value, onChange, placeholder }) => (
//     <div>
//         <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
//             {label}
//         </label>
//         <input
//             type="text"
//             value={value || ""}
//             onChange={(e) => onChange(e.target.value)}
//             className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
//             placeholder={placeholder}
//         />
//     </div>
// );

// const ReportHeaderCreateCard = ({
//     selectedTemplate,
//     reportMeta,
//     setReportMeta,
//     projectId,
//     currentUserProfile,
//     isInternalMaker,
//     contractors,
//     contractorsLoading,
// }) => {
//     const templateMeta = selectedTemplate?.report_header_meta || {};

//     const reportPrefix = String(
//         templateMeta.inspection_report_prefix ||
//         templateMeta.inspection_report_no ||
//         ""
//     )
//         .trim()
//         .replace(/-$/, "");

//     const externalContractorName =
//         currentUserProfile?.contractor_name ||
//         templateMeta.name_of_contractor ||
//         "";

//     return (
//         <div className="mb-4 rounded-xl border bg-card p-5 shadow-sm">
//             <div className="mb-4">
//                 <h3 className="text-sm font-semibold text-foreground">
//                     Inspection Details
//                 </h3>
//                 {/* <p className="mt-1 text-xs text-muted-foreground">
//                     Report header details. Only input fields are editable.
//                 </p> */}
//             </div>

//             <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
//                 <ReadOnlyHeaderField
//                     label="Format No."
//                     value={templateMeta.format_no}
//                 />

//                 <ReadOnlyHeaderField
//                     label="Revision No."
//                     value={templateMeta.revision_no || "R01"}
//                 />

//                 <ReadOnlyHeaderField
//                     label="Issued Date"
//                     value={templateMeta.issued_date}
//                 />

//                 <ReadOnlyHeaderField
//                     label="Revision Date"
//                     value={templateMeta.revision_date}
//                 />

//                 <ReadOnlyHeaderField
//                     label="Project"
//                     value={templateMeta.project || (projectId ? `Project ${projectId}` : "")}
//                 />

//                 <ReadOnlyHeaderField
//                     label="Inspection Report No."
//                     value={
//                         reportPrefix
//                             ? `${reportPrefix}-Auto`
//                             : "Auto-generated on submit"
//                     }
//                 />

//                 <ReadOnlyHeaderField
//                     label="Date of Inspection"
//                     value={formatTodayDDMMYYYY()}
//                 />

//                 {isInternalMaker ? (
//                     <div>
//                         <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
//                             Name of Contractor
//                         </label>

//                         <select
//                             value={reportMeta.name_of_contractor}
//                             onChange={(e) =>
//                                 setReportMeta((prev) => ({
//                                     ...prev,
//                                     name_of_contractor: e.target.value,
//                                 }))
//                             }
//                             disabled={contractorsLoading}
//                             className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
//                         >
//                             <option value="">
//                                 {contractorsLoading
//                                     ? "Loading contractors..."
//                                     : "Select Contractor"}
//                             </option>

//                             {contractors.map((contractor) => {
//                                 const name =
//                                     typeof contractor === "string"
//                                         ? contractor
//                                         : contractor?.name || contractor?.contractor_name || "";

//                                 if (!name) return null;

//                                 return (
//                                     <option key={name} value={name}>
//                                         {name}
//                                     </option>
//                                 );
//                             })}
//                         </select>

//                         {!contractorsLoading && contractors.length === 0 && (
//                             <p className="mt-1 text-xs text-red-500">
//                                 No contractor found in this project.
//                             </p>
//                         )}
//                     </div>
//                 ) : (
//                     <ReadOnlyHeaderField
//                         label="Name of Contractor"
//                         value={externalContractorName}
//                     />
//                 )}

//                 <EditableHeaderInput
//                     label="Make / Model"
//                     value={reportMeta.make_model}
//                     placeholder="Enter make / model"
//                     onChange={(value) =>
//                         setReportMeta((prev) => ({
//                             ...prev,
//                             make_model: value,
//                         }))
//                     }
//                 />

//                 <EditableHeaderInput
//                     label="Identification No."
//                     value={reportMeta.identification_no}
//                     placeholder="Enter identification no."
//                     onChange={(value) =>
//                         setReportMeta((prev) => ({
//                             ...prev,
//                             identification_no: value,
//                         }))
//                     }
//                 />

//                 <EditableHeaderInput
//                     label="Location"
//                     value={reportMeta.location}
//                     placeholder="Enter location"
//                     onChange={(value) =>
//                         setReportMeta((prev) => ({
//                             ...prev,
//                             location: value,
//                         }))
//                     }
//                 />

//                 <EditableHeaderInput
//                     label="Name of Operator"
//                     value={reportMeta.name_of_operator}
//                     placeholder="Enter operator name"
//                     onChange={(value) =>
//                         setReportMeta((prev) => ({
//                             ...prev,
//                             name_of_operator: value,
//                         }))
//                     }
//                 />
//             </div>

//             {isInternalMaker && (
//                 <p className="mt-3 text-xs text-gray-500">
//                     Select the Contractor Name accordingly.
//                 </p>
//             )}
//         </div>
//     );
// };




const LEGACY_HEADER_FIELDS = [
    {
        key: "format_no",
        label: "Format No.",
        source: "TEMPLATE_FIXED",
        input_type: "text",
        system_key: "",
        editable_by_maker: false,
        row_index: 1,
        column_index: 1,
        order_index: 1,
    },
    {
        key: "revision_no",
        label: "Revision No.",
        source: "TEMPLATE_FIXED",
        input_type: "text",
        system_key: "",
        editable_by_maker: false,
        row_index: 1,
        column_index: 2,
        order_index: 2,
    },
    {
        key: "issued_date",
        label: "Issued Date",
        source: "TEMPLATE_FIXED",
        input_type: "date",
        system_key: "",
        editable_by_maker: false,
        row_index: 2,
        column_index: 1,
        order_index: 3,
    },
    {
        key: "revision_date",
        label: "Revision Date",
        source: "TEMPLATE_FIXED",
        input_type: "date",
        system_key: "",
        editable_by_maker: false,
        row_index: 2,
        column_index: 2,
        order_index: 4,
    },
    {
        key: "project",
        label: "Project",
        source: "PROJECT_CONTEXT",
        input_type: "text",
        system_key: "project_name",
        editable_by_maker: false,
        row_index: 3,
        column_index: 1,
        order_index: 5,
    },
    {
        key: "inspection_report_no",
        label: "Inspection Report No.",
        source: "SYSTEM_GENERATED",
        input_type: "text",
        system_key: "report_no",
        editable_by_maker: false,
        row_index: 3,
        column_index: 2,
        order_index: 6,
    },
    {
        key: "name_of_contractor",
        label: "Name of Contractor",
        source: "MAKER_INPUT",
        input_type: "text",
        system_key: "",
        editable_by_maker: true,
        row_index: 4,
        column_index: 1,
        order_index: 7,
    },
    {
        key: "date_of_inspection",
        label: "Date of Inspection",
        source: "SYSTEM_GENERATED",
        input_type: "date",
        system_key: "inspection_date",
        editable_by_maker: false,
        row_index: 4,
        column_index: 2,
        order_index: 8,
    },
    {
        key: "make_model",
        label: "Make / Model",
        source: "MAKER_INPUT",
        input_type: "text",
        system_key: "",
        editable_by_maker: true,
        row_index: 5,
        column_index: 1,
        order_index: 9,
    },
    {
        key: "identification_no",
        label: "Identification No.",
        source: "MAKER_INPUT",
        input_type: "text",
        system_key: "",
        editable_by_maker: true,
        row_index: 5,
        column_index: 2,
        order_index: 10,
    },
    {
        key: "location",
        label: "Location",
        source: "MAKER_INPUT",
        input_type: "text",
        system_key: "",
        editable_by_maker: true,
        row_index: 6,
        column_index: 1,
        order_index: 11,
    },
    {
        key: "name_of_operator",
        label: "Name of Operator",
        source: "MAKER_INPUT",
        input_type: "text",
        system_key: "",
        editable_by_maker: true,
        row_index: 6,
        column_index: 2,
        order_index: 12,
    },
];

const formatTodayDDMMYYYY = () => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, "0");
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const yyyy = now.getFullYear();

    return `${dd}-${mm}-${yyyy}`;
};

const sortHeaderFields = (fields = []) =>
    [...fields].sort(
        (a, b) =>
            Number(a.row_index || 0) - Number(b.row_index || 0) ||
            Number(a.column_index || 0) - Number(b.column_index || 0) ||
            Number(a.order_index || 0) - Number(b.order_index || 0)
    );

const buildLegacyHeaderFields = (meta = {}) =>
    LEGACY_HEADER_FIELDS.map((field) => ({
        ...field,
        default_value: String(meta?.[field.key] || "").trim(),
        required: false,
        visible_in_report: true,
        visible_in_preview: true,
        validation_config: {},
    }));

const getTemplateHeaderFields = (template) => {
    const configuredFields = Array.isArray(template?.header_fields)
        ? template.header_fields
        : [];

    if (configuredFields.length > 0) {
        return sortHeaderFields(configuredFields);
    }

    return sortHeaderFields(
        buildLegacyHeaderFields(template?.report_header_meta || {})
    );
};

const getChecklistResolvedHeaderFields = (detail) => {
    const configuredFields = Array.isArray(detail?.report_header_fields)
        ? detail.report_header_fields
        : [];

    if (configuredFields.length > 0) {
        return sortHeaderFields(configuredFields);
    }

    const meta =
        detail?.report_header_meta ||
        detail?.safety_report_meta ||
        detail?.report_meta ||
        {};

    return sortHeaderFields(
        buildLegacyHeaderFields(meta).map((field) => ({
            ...field,
            value: String(meta?.[field.key] || "").trim(),
        }))
    );
};

const isEditableMakerField = (field) =>
    field?.source === "MAKER_INPUT" &&
    field?.editable_by_maker === true;

const isRequiredMakerField = (field) => {
    if (!isEditableMakerField(field)) {
        return false;
    }

    /*
        Business rule:
        Any Maker-input field shown on the checklist creation form is mandatory
        unless the setup explicitly marks it optional through validation_config.optional.
    */
    return field?.validation_config?.optional !== true;
};

const getExternalContractorName = (currentUserProfile = {}) =>
    String(
        currentUserProfile?.contractor_name ||
        currentUserProfile?.contractor?.name ||
        ""
    ).trim();

const buildInitialReportMeta = (template, currentUserProfile = {}) => {
    const nextMeta = {};
    const externalContractorName =
        getExternalContractorName(currentUserProfile);

    getTemplateHeaderFields(template)
        .filter(isEditableMakerField)
        .forEach((field) => {
            let initialValue = String(field.default_value || "").trim();

            if (field.key === "name_of_contractor" && externalContractorName) {
                initialValue = externalContractorName;
            }

            nextMeta[field.key] = initialValue;
        });

    return nextMeta;
};

const resolveTemplateFieldDisplayValue = ({
    field,
    selectedTemplate,
    projectId,
    projectName,
    projectConfigs,
}) => {
    const defaultValue = String(field?.default_value || "").trim();

    if (field.source === "TEMPLATE_FIXED") {
        return defaultValue;
    }

    if (field.source === "PROJECT_CONTEXT") {
        if (field.system_key === "project_name") {
            return (
                defaultValue ||
                selectedTemplate?.project_name ||
                (projectName ? projectName : (projectId ? `Project ${projectId}` : ""))
            );
        }

        return defaultValue;
    }

    if (field.source === "SYSTEM_GENERATED") {
        if (field.system_key === "report_no") {
            return getReportNumberPreview(selectedTemplate, projectId, projectConfigs);
        }

        if (
            field.system_key === "inspection_date" ||
            field.system_key === "created_date"
        ) {
            return formatTodayDDMMYYYY();
        }

        return "Auto-generated";
    }

    return defaultValue;
};

const ReadOnlyHeaderField = ({ label, value }) => (
    <div className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {label}
        </p>

        <p className="mt-1 break-words text-sm font-semibold text-foreground">
            {String(value || "").trim() || "—"}
        </p>
    </div>
);

const EditableHeaderInput = ({ field, value, onChange }) => {
    const options = Array.isArray(field?.validation_config?.options)
        ? field.validation_config.options
        : [];

    if (field.input_type === "select") {
        return (
            <div>
                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {field.label}
                    {isRequiredMakerField(field) ? (
                        <span className="ml-0.5 text-red-500">*</span>
                    ) : null}
                </label>

                <select
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                >
                    <option value="">Select {field.label}</option>

                    {options.map((option) => {
                        const optionValue =
                            typeof option === "string"
                                ? option
                                : option?.value || option?.label || "";

                        const optionLabel =
                            typeof option === "string"
                                ? option
                                : option?.label || option?.value || "";

                        if (!optionValue) return null;

                        return (
                            <option key={optionValue} value={optionValue}>
                                {optionLabel}
                            </option>
                        );
                    })}
                </select>
            </div>
        );
    }

    const inputType = ["date", "number"].includes(field.input_type)
        ? field.input_type
        : "text";

    return (
        <div>
            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {field.label}
                {isRequiredMakerField(field) ? (
                    <span className="ml-0.5 text-red-500">*</span>
                ) : null}
            </label>

            <input
                type={inputType}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                placeholder={`Enter ${String(field.label || "").toLowerCase()}`}
            />
        </div>
    );
};

const ReportHeaderInfoCard = ({ detail }) => {
    const resolvedFields = getChecklistResolvedHeaderFields(detail).filter(
        (field) => field.visible_in_preview !== false
    );

    return (
        <div className="mb-6 rounded-xl border bg-card p-5 shadow-sm">
            <h2 className="mb-4 text-sm font-bold text-foreground">
                Checklist Details
            </h2>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {resolvedFields.map((field) => (
                    <ReadOnlyHeaderField
                        key={field.key}
                        label={field.label}
                        value={
                            field.value ??
                            field.default_value ??
                            ""
                        }
                    />
                ))}
            </div>
        </div>
    );
};

const ReportHeaderCreateCard = ({
    selectedTemplate,
    reportMeta,
    setReportMeta,
    projectId,
    projectConfigs,
    currentUserProfile,
    isInternalMaker,
    contractors,
    contractorsLoading,
    projectName,
}) => {
    const configuredFields = getTemplateHeaderFields(selectedTemplate);
    const externalContractorName =
        getExternalContractorName(currentUserProfile);

    return (
        <div className="mb-4 rounded-xl border bg-card p-5 shadow-sm">
            <div className="mb-4">
                <h3 className="text-sm font-semibold text-foreground">
                    Inspection Details
                </h3>

                <p className="mt-1 text-xs text-muted-foreground">
                    Complete the inspection details below before answering the checklist questions. Required fields marked with * must be filled and will be included in the generated report.
                </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {configuredFields.map((field, index) => {
                    const editableByMaker = isEditableMakerField(field);

                    if (!editableByMaker) {
                        return (
                            <ReadOnlyHeaderField
                                key={field.id || `field-${index}`}
                                label={field.label}
                                value={resolveTemplateFieldDisplayValue({
                                    field,
                                    selectedTemplate,
                                    projectId,
                                    projectName,
                                    projectConfigs,
                                })}
                            />
                        );
                    }

                    if (
                        field.key === "name_of_contractor" &&
                        isInternalMaker
                    ) {
                        return (
                            <div key={field.key}>
                                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                    {field.label}
                                    {field.required ? " *" : ""}
                                </label>

                                <select
                                    value={reportMeta[field.key] || ""}
                                    onChange={(e) =>
                                        setReportMeta((prev) => ({
                                            ...prev,
                                            [field.key]: e.target.value,
                                        }))
                                    }
                                    disabled={contractorsLoading}
                                    className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                                >
                                    <option value="">
                                        {contractorsLoading
                                            ? "Loading contractors..."
                                            : "Select Contractor"}
                                    </option>

                                    {contractors.map((contractor) => {
                                        const name =
                                            typeof contractor === "string"
                                                ? contractor
                                                : contractor?.name ||
                                                contractor?.contractor_name ||
                                                "";

                                        if (!name) return null;

                                        return (
                                            <option key={name} value={name}>
                                                {name}
                                            </option>
                                        );
                                    })}
                                </select>

                                {!contractorsLoading &&
                                    contractors.length === 0 && (
                                        <p className="mt-1 text-xs text-red-500">
                                            No contractor found in this project.
                                        </p>
                                    )}
                            </div>
                        );
                    }

                    if (
                        field.key === "name_of_contractor" &&
                        !isInternalMaker
                    ) {
                        return (
                            <ReadOnlyHeaderField
                                key={field.key}
                                label={field.label}
                                value={
                                    reportMeta[field.key] ||
                                    externalContractorName ||
                                    field.default_value ||
                                    ""
                                }
                            />
                        );
                    }

                    return (
                        <EditableHeaderInput
                            key={field.key}
                            field={field}
                            value={reportMeta[field.key] || ""}
                            onChange={(value) =>
                                setReportMeta((prev) => ({
                                    ...prev,
                                    [field.key]: value,
                                }))
                            }
                        />
                    );
                })}
            </div>

            {isInternalMaker &&
                configuredFields.some(
                    (field) =>
                        field.key === "name_of_contractor" &&
                        isEditableMakerField(field)
                ) && (
                    <p className="mt-3 text-xs text-gray-500">
                        Select the Contractor Name accordingly.
                    </p>
                )}
        </div>
    );
};

export const isoToDisplayDate = (val) => {
    if (!val || typeof val !== "string") return "";
    const str = val.trim();
    if (!str) return "";
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) return str;
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        const [yyyy, mm, dd] = str.split("-");
        return `${dd}/${mm}/${yyyy}`;
    }
    if (/^\d{4}\/\d{2}\/\d{2}$/.test(str)) {
        const [yyyy, mm, dd] = str.split("/");
        return `${dd}/${mm}/${yyyy}`;
    }
    if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
        const [dd, mm, yyyy] = str.split("-");
        return `${dd}/${mm}/${yyyy}`;
    }
    return str;
};

export const displayToIsoDate = (val) => {
    if (!val || typeof val !== "string") return "";
    const str = val.trim();
    if (!str) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
        const [dd, mm, yyyy] = str.split("/");
        return `${yyyy}-${mm}-${dd}`;
    }
    if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
        const [dd, mm, yyyy] = str.split("-");
        return `${yyyy}-${mm}-${dd}`;
    }
    if (/^\d{4}\/\d{2}\/\d{2}$/.test(str)) {
        const [yyyy, mm, dd] = str.split("/");
        return `${yyyy}-${mm}-${dd}`;
    }
    return "";
};



// ─────────────────────────────────────────────
// MakerDashboard
// ─────────────────────────────────────────────
export default function MakerDashboard() {
    // Helper to safely parse report_layout which might be a stringified JSON
    const getTableStyle = (template) => {
        if (!template?.report_layout) return null;
        let layout = template.report_layout;
        if (typeof layout === 'string') {
            try {
                layout = JSON.parse(layout);
            } catch (e) {
                return null;
            }
        }
        if (layout?.is_horizontal === true || layout?.table_style === "horizontal") {
            return "horizontal";
        }
        return null;
    };

    const userId = getCurrentUserId();

    const [projectId, setProjectId] = useState("");
    const [projectName, setProjectName] = useState("");
    const [orgId, setOrgId] = useState("");
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState("");
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateLoading, setTemplateLoading] = useState(false);
    const [createAnswers, setCreateAnswers] = useState({});
    const [createRemarks, setCreateRemarks] = useState({});
    const [createMedia, setCreateMedia] = useState({});

    const [horizontalRows, setHorizontalRows] = useState([1]);
    const [expandedRows, setExpandedRows] = useState({ 1: true });
    const [reportMeta, setReportMeta] = useState({});

    const [filters, setFilters] = useState({
        fromDate: "",
        toDate: "",
        contractor: "all",
        status: "all",
        type: "all",
    });

    const [checklists, setChecklists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [view, setView] = useState("dashboard"); // "dashboard" | "maker_fix"
    const [makerRemarks, setMakerRemarks] = useState({});
    const [makerMedia, setMakerMedia] = useState({});
    const [makerAnswers, setMakerAnswers] = useState({});
    const [isMakerSignatureModalOpen, setIsMakerSignatureModalOpen] = useState(false);
    const [pendingSubmitPayload, setPendingSubmitPayload] = useState(null);
    const [signatureSubmitting, setSignatureSubmitting] = useState(false);

    const [currentUserProfile, setCurrentUserProfile] = useState({});
    const [contractors, setContractors] = useState([]);
    const [contractorsLoading, setContractorsLoading] = useState(false);
    const [projectConfigs, setProjectConfigs] = useState(() => {
        try {
            const cached = JSON.parse(
                localStorage.getItem("SAFETY_PROJECT_REPORT_CONFIGS") || "{}"
            );
            const map = {};
            Object.entries(cached).forEach(([pid, code]) => {
                map[pid] = { project_id: Number(pid), project_code: code };
            });
            return map;
        } catch {
            return {};
        }
    });

    const [signaturePurpose, setSignaturePurpose] = useState(null);
    const [pendingCreateSubmitFormData, setPendingCreateSubmitFormData] = useState(null);

    const [historyChecklist, setHistoryChecklist] = useState(null);

    const [readonlyChecklist, setReadonlyChecklist] = useState(null);
    const [readonlyPendingStatusLabel, setReadonlyPendingStatusLabel] = useState("Pending for Review");

    const currentUserType = String(currentUserProfile?.user_type || "").toUpperCase();
    const isInternalMaker = currentUserType === "INTERNAL";

    const sigCanvasRef = useRef(null);
    // Reusable component handles the resize logic now

    // ── data fetching ───────────────────────────────────────────
    const fetchList = async () => {
        if (!userId) return;
        setLoading(true);
        setError(null);
        try {
            const params = { assigned_to_me: true, template_type: "SAFETY" };
            if (projectId) params.project_id = projectId;
            const res = await listSafetyChecklists(params);
            const data = res?.data;
            setChecklists(Array.isArray(data) ? data : data?.results ?? []);
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || "Failed to load checklists.";
            setError(msg);
            showToast(msg, "error");
        } finally {
            setLoading(false);
        }
    };


    const loadCreateTemplates = async () => {
        let effectiveProjectId = String(projectId || "").trim();
        let effectiveOrgId = String(orgId || "").trim();

        // Important: fresh login case.
        // If state is empty, resolve again from access token / USER_DATA / localStorage.
        if (!effectiveProjectId || !effectiveOrgId) {
            const ctx = resolveMakerContext();

            effectiveProjectId = ctx.projectId;
            effectiveOrgId = ctx.orgId;

            setProjectId(effectiveProjectId);
            setOrgId(effectiveOrgId);
            setProjectName(ctx.projectName);
            setCurrentUserProfile(ctx.profile);
        }

        if (!effectiveProjectId || !effectiveOrgId) {
            setTemplates([]);
            showToast(
                "Project or organization not found. Please select/open your project profile once.",
                "error"
            );
            return;
        }

        setTemplatesLoading(true);

        try {
            const catRes = await listSafetyCategories({
                org_id: effectiveOrgId,
                project_id: effectiveProjectId,
                active: true,
            });

            const catRaw = catRes?.data;
            const categories = Array.isArray(catRaw) ? catRaw : catRaw?.results ?? [];

            const safetyCategory =
                categories.find(
                    (c) => String(c.name || "").trim().toLowerCase() === "safety checklist"
                ) || categories[0];

            if (!safetyCategory) {
                setTemplates([]);
                showToast("Safety Checklist category not found.", "error");
                return;
            }

            const tplRes = await listSafetyTemplates({
                org_id: effectiveOrgId,
                project_id: effectiveProjectId,
                category: safetyCategory.id,
                status: "ACTIVE",
                is_latest: true,
            });

            const tplRaw = tplRes?.data;
            const list = Array.isArray(tplRaw) ? tplRaw : tplRaw?.results ?? [];

            setTemplates(list);

            try {
                const confRes = await listSafetyProjectReportConfigs({
                    org_id: effectiveOrgId,
                });
                const confList = Array.isArray(confRes?.data)
                    ? confRes.data
                    : confRes?.data?.results ?? [];
                const confMap = {};
                const cacheMap = {};
                confList.forEach((c) => {
                    confMap[c.project_id] = c;
                    if (c.project_id && c.project_code) {
                        cacheMap[c.project_id] = c.project_code;
                    }
                });
                setProjectConfigs(confMap);
                try {
                    localStorage.setItem(
                        "SAFETY_PROJECT_REPORT_CONFIGS",
                        JSON.stringify(cacheMap)
                    );
                } catch {}
            } catch (confErr) {
                console.error("Failed to load project report configs in maker dashboard:", confErr);
            }
        } catch (err) {
            setTemplates([]);
            showToast(
                err?.response?.data?.detail || "Failed to load safety checklists.",
                "error"
            );
        } finally {
            setTemplatesLoading(false);
        }
    };


    const loadContractorNames = async () => {
        if (!orgId || !projectId || !isInternalMaker) {
            setContractors([]);
            return;
        }

        setContractorsLoading(true);

        try {
            const res = await listContractorNamesByOrg(orgId, projectId);
            const raw = res?.data;

            const list = Array.isArray(raw) ? raw : raw?.results ?? [];

            setContractors(list);
        } catch (err) {
            setContractors([]);
            showToast(
                err?.response?.data?.detail || "Failed to load contractor names.",
                "error"
            );
        } finally {
            setContractorsLoading(false);
        }
    };

    useEffect(() => {
        if (view === "create" && orgId && projectId) {
            loadCreateTemplates();
        }
    }, [view, orgId, projectId]);

    useEffect(() => {
        if (view === "create" && orgId && projectId && isInternalMaker) {
            loadContractorNames();
        }
    }, [view, orgId, projectId, isInternalMaker]);



    useEffect(() => {
        if (!selectedTemplateId) {
            setSelectedTemplate(null);
            setCreateAnswers({});
            setCreateRemarks({});
            setCreateMedia({});
            setReportMeta({});
            return;
        }

        let alive = true;

        const loadTemplateDetail = async () => {
            setTemplateLoading(true);

            try {
                const res = await getSafetyTemplate(selectedTemplateId);
                const data = res?.data || null;

                if (!alive) return;

                setSelectedTemplate(data);

                const headerMeta = data?.report_header_meta || {};

                setReportMeta(
                    buildInitialReportMeta(
                        data,
                        currentUserProfile
                    )
                );

                const initialAnswers = {};
                const initialRemarks = {};

                const tableStyle = getTableStyle(data);
                if (tableStyle === "matrix") {
                    let layoutObj = data.report_layout;
                    if (typeof layoutObj === 'string') layoutObj = JSON.parse(layoutObj);
                    const colsCount = Number(layoutObj.matrix_columns) || 31;
                    
                    const matrixCols = [];
                    for (let i = 1; i <= colsCount; i++) matrixCols.push(i);
                    setHorizontalRows(matrixCols);

                    (data?.questions || []).forEach((q) => {
                        matrixCols.forEach(cIdx => {
                            const keyId = `${q.id}_${cIdx}`;
                            initialAnswers[keyId] = "";
                            initialRemarks[keyId] = "";
                        });
                    });
                } else if (tableStyle === "horizontal") {
                    (data?.questions || []).forEach((q) => {
                        const keyId = `${q.id}_1`;
                        if (q.type === "date" && Array.isArray(q.options) && q.options.includes("autoFetchOneMonth")) {
                            const d = new Date();
                            d.setMonth(d.getMonth() + 1);
                            initialAnswers[keyId] = d.toISOString().split("T")[0];
                        } else {
                            initialAnswers[keyId] = "";
                        }
                        initialRemarks[keyId] = "";
                    });
                } else {
                    (data?.questions || []).forEach((q) => {
                        if (q.type === "date" && Array.isArray(q.options) && q.options.includes("autoFetchOneMonth")) {
                            const d = new Date();
                            d.setMonth(d.getMonth() + 1);
                            initialAnswers[q.id] = d.toISOString().split("T")[0];
                        } else {
                            initialAnswers[q.id] = "";
                        }
                        initialRemarks[q.id] = "";
                    });
                }

                setCreateAnswers(initialAnswers);
                setCreateRemarks(initialRemarks);
                setCreateMedia({});
            } catch (err) {
                showToast(
                    err?.response?.data?.detail || "Failed to load checklist questions.",
                    "error"
                );
            } finally {
                if (alive) setTemplateLoading(false);
            }
        };

        loadTemplateDetail();

        return () => {
            alive = false;
        };
    }, [selectedTemplateId]);


    useEffect(() => {
        if (!selectedTemplate || isInternalMaker) {
            return;
        }

        const contractorField = getTemplateHeaderFields(selectedTemplate).find(
            (field) =>
                field.key === "name_of_contractor" &&
                isEditableMakerField(field)
        );

        if (!contractorField) {
            return;
        }

        const externalContractorName =
            getExternalContractorName(currentUserProfile);

        if (!externalContractorName) {
            return;
        }

        setReportMeta((previous) => ({
            ...previous,
            name_of_contractor:
                previous.name_of_contractor || externalContractorName,
        }));
    }, [selectedTemplate, currentUserProfile, isInternalMaker]);


    useEffect(() => {
        const ctx = resolveMakerContext();

        setProjectId(ctx.projectId);
        setOrgId(ctx.orgId);
        setProjectName(ctx.projectName);
        setCurrentUserProfile(ctx.profile);
    }, []);

    useEffect(() => {
        console.log("MAKER USER PROFILE:", currentUserProfile);
        console.log("IS INTERNAL MAKER:", isInternalMaker);
    }, [currentUserProfile, isInternalMaker]);

    useEffect(() => {
        fetchList();
    }, [userId, projectId]);


    const openCreateChecklist = () => {
        const ctx = resolveMakerContext();

        if (!ctx.projectId || !ctx.orgId) {
            showToast(
                "Project or organization not found. Please select/open your project profile once.",
                "error"
            );
            return;
        }

        setProjectId(ctx.projectId);
        setOrgId(ctx.orgId);
        setProjectName(ctx.projectName);
        setCurrentUserProfile(ctx.profile);

        setSelectedTemplateId("");
        setSelectedTemplate(null);
        setCreateAnswers({});
        setCreateRemarks({});
        setCreateMedia({});
        setHorizontalRows([1]);
        setReportMeta({});
        setContractors([]);
        setView("create");
    }

    // const backFromCreate = () => {
    //     setSelectedTemplateId("");
    //     setSelectedTemplate(null);
    //     setCreateAnswers({});
    //     setCreateRemarks({});
    //     setCreateMedia({});
    //     setReportMeta({
    //         make_model: "",
    //         identification_no: "",
    //         location: "",
    //         name_of_operator: "",
    //         name_of_contractor: "",
    //     });
    //     setView("dashboard");
    // };


    const backFromCreate = () => {
        setSelectedTemplateId("");
        setSelectedTemplate(null);
        setCreateAnswers({});
        setCreateRemarks({});
        setCreateMedia({});
        setHorizontalRows([1]);
        setReportMeta({});
        setView("dashboard");
    };


    // ── derived lists ───────────────────────────────────────────
    const isMakerTurn = (c) =>
        (c.current_assignee_role || "").toUpperCase() === "MAKER" &&
        Number(c.current_assignee_id) === Number(userId);

    const makerInWorkflow = (c) => Boolean(c?.maker_in_workflow);

    const rejectedPoints = (c) => Number(c.maker_rejected_points ?? 0);

    const statusOf = (c) => String(c.status || "").toLowerCase();

    const currentRoleOf = (c) =>
        String(c.current_assignee_role || "").toUpperCase();

    const reviewRoundCount = (c) =>
        Number(c?.review_state?.completed_review_rounds ?? 0);

    const isClosedRejected = (c) => statusOf(c) === "closed_rejected";

    const isFinalChecklist = (c) =>
        statusOf(c) === "completed" || statusOf(c) === "closed_rejected";

    const isRectificationCycle = (c) =>
        reviewRoundCount(c) > 0 || rejectedPoints(c) > 0;

    const makerCanRectify = (c) =>
        makerInWorkflow(c) &&
        isMakerTurn(c) &&
        rejectedPoints(c) > 0;

    // Pending buckets should NOT be affected by filters.
    // So use raw checklists for pending/review/rectification.
    const makerPendingReview = checklists.filter(
        (c) =>
            makerInWorkflow(c) &&
            !isFinalChecklist(c) &&
            statusOf(c) === "in_progress" &&
            currentRoleOf(c) === "CHECKER" &&
            !isRectificationCycle(c)
    );

    const makerPendingRectification = checklists.filter(
        (c) =>
            makerInWorkflow(c) &&
            !isFinalChecklist(c) &&
            statusOf(c) === "in_progress" &&
            isRectificationCycle(c)
    );

    // Raw completed list.
    // Filters will apply only on this completed list.
    const makerCompletedRaw = checklists.filter(
        (c) => makerInWorkflow(c) && isFinalChecklist(c)
    );

    // Filter options should also come from completed checklists only.
    const contractorOptions = Array.from(
        new Set(makerCompletedRaw.map(getChecklistContractor).filter(Boolean))
    ).sort();

    const typeOptions = Array.from(
        new Set(makerCompletedRaw.map(getChecklistType).filter(Boolean))
    ).sort();

    // Final completed list after date/contractor/status/type filter.
    const makerCompletedChecklists = applyChecklistFilters(
        makerCompletedRaw,
        filters
    );

    // These counters are affected by filters because they belong to completed bucket.
    const makerApprovedCount = makerCompletedChecklists.filter(isApprovedChecklist).length;

    const makerRejectedCount = makerCompletedChecklists.filter(isRejectedChecklist).length;

    const safeCount = (value) => {
        const numberValue = Number(value);
        return Number.isFinite(numberValue) ? numberValue : 0;
    };

    const makerTotalCount =
        safeCount(makerPendingReview?.length) +
        safeCount(makerPendingRectification?.length) +
        safeCount(makerApprovedCount) +
        safeCount(makerRejectedCount);


    const totalQuestionsLabel = (c) => {
        const n = c.items_count;
        if (n == null || Number.isNaN(Number(n))) return null;
        const t = Number(n);
        return `${t} question${t === 1 ? "" : "s"}`;
    };

    const formatChecklistDateTime = (value) => {
        if (!value) return "";

        const date = new Date(value);

        return date.toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    };

    const rejectedPointsLabel = (c) => {
        const n = rejectedPoints(c);
        if (n <= 0) return null;
        return `${n} rejected point${n === 1 ? "" : "s"}`;
    };

    const MAKER_REJECTED_STATUSES = [
        "rejected_by_checker",
        "rejected_by_supervisor",
    ];

    const makerFixItems =
        detail?.items
            ?.flatMap((item) =>
                (item.submissions || []).map(sub => ({
                    item,
                    sub,
                    editable: MAKER_REJECTED_STATUSES.includes(sub.status),
                    highlighted: MAKER_REJECTED_STATUSES.includes(sub.status),
                }))
            )
            .filter(Boolean) ?? [];

    const makerEditableItems = makerFixItems.filter((row) => row.editable);

    const fixHorizontalRows = Array.from(new Set(makerFixItems.map(x => Number(x.sub?.row_index || 1)))).sort((a, b) => a - b);
    const isFixHorizontal = fixHorizontalRows.length > 1 || makerFixItems.some(x => (x.item?.submissions || []).length > 1);

    const toggleFixRow = (rowIdx) => setExpandedRows(prev => ({ ...prev, [`fix_${rowIdx}`]: !prev[`fix_${rowIdx}`] }));

    const renderFixAccordionWrapper = (rowIdx, arrayIdx, itemsRenderer) => {
        const isExpanded = expandedRows[`fix_${rowIdx}`] !== false; // Default expanded
        return (
            <div key={rowIdx} className="rounded-xl border bg-card shadow-sm overflow-hidden mb-4">
                <div
                    className="flex items-center justify-between bg-muted/30 px-5 py-4 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => toggleFixRow(rowIdx)}
                >
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-100 text-orange-600 font-bold">
                            {arrayIdx + 1}
                        </div>
                        <h3 className="font-bold text-foreground">
                                                                            {getTableStyle(selectedTemplate) === "matrix" 
                                                                                ? (selectedTemplate?.report_layout?.matrix_type === "MATRIX_WEEKLY" ? `Week ${arrayIdx + 1}` : `Day ${arrayIdx + 1}`) 
                                                                                : `Item ${arrayIdx + 1}`}
                                                                        </h3>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="text-muted-foreground">
                            {isExpanded ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                            )}
                        </div>
                    </div>
                </div>
                {isExpanded && (
                    <div className="p-5 border-t space-y-4 bg-gray-50/30">
                        {itemsRenderer()}
                    </div>
                )}
            </div>
        );
    };

    const renderFixItemList = (itemsList, renderer) => {
        if (isFixHorizontal) {
            return (
                <div className="space-y-4">
                    {fixHorizontalRows.map((rowIdx, arrayIdx) => {
                        const rowItems = itemsList.filter(x => Number(x.sub?.row_index || 1) === rowIdx);
                        return renderFixAccordionWrapper(rowIdx, arrayIdx, () => rowItems.map((item, idx) => renderer(item, idx, idx + 1)));
                    })}
                </div>
            );
        }
        const sectionedRows = buildSectionedQuestionRows({
            questions: itemsList,
            sectionConfig: detail?.template_section_config,
            getOrderIndex: (entry) => entry?.item?.template_order_index,
        });
        return (
            <div className="space-y-4">
                {sectionedRows.map((entry, idx) => {
                    if (entry.type === "section") {
                        return (
                            <SafetyChecklistSectionHeader
                                key={`sec-${entry.key || idx}`}
                                label={entry.label}
                            />
                        );
                    }
                    return renderer(entry.question, entry.serial_no - 1, entry.serial_no);
                })}
            </div>
        );
    };

    // ── navigation ──────────────────────────────────────────────
    const backToDashboard = () => {
        setView("dashboard");
        setDetail(null);
        setMakerRemarks({});
        setMakerMedia({});
        setMakerAnswers({});
        setIsMakerSignatureModalOpen(false);
        setPendingSubmitPayload(null);
        if (sigCanvasRef.current) sigCanvasRef.current.clear();
    };

    // ── open fix view ───────────────────────────────────────────
    const openMakerFix = async (cl) => {
        setDetail(null);
        setDetailLoading(true);
        setMakerRemarks({});
        setMakerMedia({});
        try {
            const res = await getSafetyChecklist(cl.id);
            const data = res?.data || null;
            setDetail(data);
            const initialRemarks = {};
            const initialAnswers = {};

            (data?.items || []).forEach((entry) => {
                (entry.submissions || []).forEach((sub) => {
                    initialRemarks[sub.id] = "";
                    initialAnswers[sub.id] = sub.latest_maker_answer || "";
                });
            });

            setMakerRemarks(initialRemarks);
            setMakerAnswers(initialAnswers);
            setView("maker_fix");
        } catch (err) {
            showToast(err?.response?.data?.detail || "Failed to load checklist", "error");
        } finally {
            setDetailLoading(false);
        }
    };

    // ── submit all fixes at once ────────────────────────────────
    // const handleSubmitAllFixes = async () => {
    //     if (!detail) return;
    //     const submissions = (detail.items || [])
    //         .map((item) => {
    //             const sub = (item.submissions || []).find((s) =>
    //                 MAKER_QUEUE_STATUSES.includes(s.status)
    //             );
    //             if (!sub) return null;
    //             const payload = { submission_id: sub.id, maker_remarks: makerRemarks[sub.id] ?? "" };
    //             const media = makerMedia[sub.id];
    //             if (media) payload.maker_media_base64 = media;
    //             return payload;
    //         })
    //         .filter(Boolean);
    //     const payload = submissions.length ? { submissions } : {};
    //     const isBulk = !submissions.length;
    //     if (shouldShowMakerSignatureModal(detail, submissions, isBulk)) {
    //         setPendingSubmitPayload(payload);
    //         setIsMakerSignatureModalOpen(true);
    //         return;
    //     }

    //     setSubmitting(true);
    //     try {
    //         await submitSafetyChecklist(detail.id, payload);
    //         showToast("Submitted successfully", "success");
    //         backToDashboard();
    //         fetchList();
    //     } catch (err) {
    //         showToast(err?.response?.data?.detail || "Submit failed", "error");
    //     } finally {
    //         setSubmitting(false);
    //     }
    // };




    // ── submit a single fix ─────────────────────────────────────

    const handleSubmitAllFixes = async () => {
        if (!detail) return;

        const submissions = makerFixItems
            .filter(({ editable }) => editable)
            .map(({ item, sub }) => {
                let ans = makerAnswers[sub.id] || "";
                const inputType = item?.template_question?.type || item?.type || "";
                if (inputType === "date") {
                    ans = isoToDisplayDate(ans);
                }
                return {
                    submission_id: sub.id,
                    answer: ans,
                    maker_remarks: makerRemarks[sub.id] ?? "",
                };
            });

        if (!submissions.length) {
            showToast("No rejected questions to submit.", "error");
            return;
        }

        const missingAnswer = submissions.find((s) => !s.answer);

        if (missingAnswer) {
            showToast("Please answer all rejected questions.", "error");
            return;
        }

        // IMPORTANT:
        // Re-submit must always go through maker signature modal.
        // Do not directly call submitSafetyChecklist here.
        setPendingSubmitPayload({ submissions });
        setSignaturePurpose("submit_fix");
        setIsMakerSignatureModalOpen(true);
    };



    // const handleSubmitSingleFix = async (submissionId) => {
    //     if (!detail) return;

    //     const submissions = [];

    //     (detail.items || []).forEach((item) => {
    //         const sub = (item.submissions || []).find((s) => s.id === submissionId);
    //         if (!sub) return;

    //         submissions.push({
    //             submission_id: sub.id,
    //             answer: makerAnswers[sub.id] || "",
    //             maker_remarks: makerRemarks[sub.id] ?? "",
    //         });
    //     });

    //     if (!submissions.length) {
    //         showToast("Could not find fix to submit.", "error");
    //         return;
    //     }

    //     if (!submissions[0]?.answer) {
    //         showToast("Please answer this question before submitting.", "error");
    //         return;
    //     }

    //     // IMPORTANT:
    //     // Single fix submit also needs maker signature.
    //     setPendingSubmitPayload({ submissions });
    //     setSignaturePurpose("submit_fix");
    //     setIsMakerSignatureModalOpen(true);
    // };


    const handleCloseMakerSignatureModal = () => {
        if (signatureSubmitting) return;

        setIsMakerSignatureModalOpen(false);
        setPendingSubmitPayload(null);
        setPendingCreateSubmitFormData(null);
        setSignaturePurpose(null);

        if (sigCanvasRef.current) sigCanvasRef.current.clear();
    };

    const handleSubmitWithMakerSignature = async () => {
        if (!detail || !pendingSubmitPayload) return;

        if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
            showToast("Please provide your signature.", "error");
            return;
        }

        const dataUrl = sigCanvasRef.current.toDataURL("image/png");
        const base64Part = dataUrl.split(",")[1] || "";

        if (!base64Part) {
            showToast("Could not capture signature. Try again.", "error");
            return;
        }

        const binary = atob(base64Part);
        const bytes = new Uint8Array(binary.length);

        for (let i = 0; i < binary.length; i += 1) {
            bytes[i] = binary.charCodeAt(i);
        }

        const signatureFile = new File(
            [bytes],
            `maker-signature-${detail.id}-${Date.now()}.png`,
            { type: "image/png" }
        );

        const formData = new FormData();

        formData.append(
            "submissions",
            JSON.stringify(pendingSubmitPayload?.submissions || [])
        );

        (pendingSubmitPayload?.submissions || []).forEach((sub) => {
            const file = makerMedia[sub.submission_id];

            if (file) {
                // Preferred explicit key, backend supports this.
                formData.append(`maker_media_${sub.submission_id}`, file);
            }
        });

        formData.append("maker_signature", signatureFile);

        // Dynamic GPS capture and geofence evaluation
        let userCoords = null;
        try {
            const targetProjectId = detail?.project_id || projectId;
            const geoCheck = await verifySitePresence({ projectId: targetProjectId });
            if (!geoCheck.allowed) {
                setSignatureSubmitting(false);
                showToast(geoCheck.message || "Location verification failed.", "error");
                return;
            }
            if (!geoCheck.isBypassed && geoCheck.coords) {
                userCoords = geoCheck.coords;
            }
        } catch (geoErr) {
            setSignatureSubmitting(false);
            const userMsg =
                geoErr.userMessage ||
                geoErr.message ||
                "Location access is required to submit a Safety Inspection. Please enable GPS permissions and try again.";
            showToast(userMsg, "error");
            return;
        }

        if (userCoords) {
            formData.append("user_latitude", String(formatCoordinate(userCoords.latitude, 7)));
            formData.append("user_longitude", String(formatCoordinate(userCoords.longitude, 7)));
            if (userCoords.accuracy !== undefined && userCoords.accuracy !== null) {
                formData.append("user_location_accuracy", String(userCoords.accuracy));
            }
        }

        setSignatureSubmitting(true);

        try {
            await submitSafetyChecklist(detail.id, formData);

            showToast("Submitted successfully", "success");

            setIsMakerSignatureModalOpen(false);
            setPendingSubmitPayload(null);
            setSignaturePurpose(null);

            if (sigCanvasRef.current) sigCanvasRef.current.clear();

            backToDashboard();
            fetchList();
        } catch (err) {
            showToast(
                err?.response?.data?.detail || "Signature submit failed",
                "error"
            );
        } finally {
            setSignatureSubmitting(false);
        }
    };


    const handleDownloadReport = async (item) => {
        if (!item?.id) return;

        try {
            const res = await downloadSafetyReport(item.id, {
                org_id: orgId,
                mode: "download",
            });

            const blob = res?.data;
            if (!blob) {
                showToast("Report file not found.", "error");
                return;
            }

            const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
            const link = document.createElement("a");

            const safeName = String(item.name || "safety-report")
                .trim()
                .replace(/[^\w.-]+/g, "-");

            link.href = url;
            link.download = `${safeName}-${item.id}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);
            showToast("Report downloaded", "success");
        } catch (err) {
            showToast(
                err?.response?.data?.detail || err?.message || "Download failed",
                "error"
            );
        }
    };


    const handleCreateAndSubmitChecklist = async () => {
        if (!selectedTemplate) {
            showToast("Please select checklist.", "error");
            return;
        }

        const editableHeaderFields = getTemplateHeaderFields(selectedTemplate).filter(
            isEditableMakerField
        );

        const cleanReportMeta = {};

        editableHeaderFields.forEach((field) => {
            let value = String(
                reportMeta[field.key] ??
                field.default_value ??
                ""
            ).trim();

            if (
                field.key === "name_of_contractor" &&
                !isInternalMaker
            ) {
                value =
                    value ||
                    getExternalContractorName(currentUserProfile);
            }

            cleanReportMeta[field.key] = value;
        });

        const missingRequiredField = editableHeaderFields.find(
            (field) =>
                isRequiredMakerField(field) &&
                !String(cleanReportMeta[field.key] || "").trim()
        );

        if (missingRequiredField) {
            showToast(`${missingRequiredField.label} is required.`, "error");
            return;
        }

        const questions = selectedTemplate?.questions || [];

        if (!questions.length) {
            showToast("Selected checklist has no questions.", "error");
            return;
        }

        let unansweredIndex = -1;
        let unansweredRowIdx = -1;

        if (getTableStyle(selectedTemplate) === "horizontal" || getTableStyle(selectedTemplate) === "matrix") {
            for (let rIdx of horizontalRows) {
                unansweredIndex = questions.findIndex((q) => !String(createAnswers[`${q.id}_${rIdx}`] || "").trim());
                if (unansweredIndex !== -1) {
                    unansweredRowIdx = rIdx;
                    break;
                }
            }
        } else {
            unansweredIndex = questions.findIndex((q) => !String(createAnswers[q.id] || "").trim());
        }

        if (unansweredIndex !== -1) {
            const rowStr = unansweredRowIdx !== -1 ? ` on Row ${horizontalRows.indexOf(unansweredRowIdx) + 1}` : "";
            showToast(
                `Please answer Question ${unansweredIndex + 1}${rowStr}`,
                "error"
            );
            return;
        }

        const missingRequiredPhoto = questions.find(
            (q) => {
                if (getTableStyle(selectedTemplate) === "horizontal") {
                    return horizontalRows.some(rowIdx => q.photo_required && !createMedia[`${q.id}_${rowIdx}`]);
                }
                return q.photo_required && !createMedia[q.id];
            }
        );

        if (missingRequiredPhoto) {
            showToast(`Photo is required for: ${missingRequiredPhoto.text}`, "error");
            return;
        }

        const formData = new FormData();

        formData.append("template_id", Number(selectedTemplate.id));
        formData.append("project_id", Number(projectId));
        formData.append("org_id", Number(orgId));
        formData.append("title", selectedTemplate.title || "Safety Checklist");

        // formData.append(
        //     "report_meta",
        //     JSON.stringify({
        //         make_model: reportMeta.make_model || "",
        //         identification_no: reportMeta.identification_no || "",
        //         location: reportMeta.location || "",
        //         name_of_operator: reportMeta.name_of_operator || "",

        //         name_of_contractor: reportMeta.name_of_contractor || "",
        //     })
        // );

        formData.append(
            "report_meta",
            JSON.stringify(cleanReportMeta)
        );

        const answersPayload = getTableStyle(selectedTemplate) === "horizontal"
            ? horizontalRows.flatMap(rowIdx =>
                questions.map((q) => {
                    let ans = createAnswers[`${q.id}_${rowIdx}`] || "";
                    if (q.type === "date") ans = isoToDisplayDate(ans);
                    return {
                        template_question_id: q.id,
                        row_index: rowIdx,
                        answer: ans,
                        maker_remarks: createRemarks[`${q.id}_${rowIdx}`] || "",
                    };
                })
            )
            : questions.map((q) => {
                let ans = createAnswers[q.id] || "";
                if (q.type === "date") ans = isoToDisplayDate(ans);
                return {
                    template_question_id: q.id,
                    answer: ans,
                    maker_remarks: createRemarks[q.id] || "",
                };
            });

        formData.append(
            "answers",
            JSON.stringify(answersPayload)
        );

        if (getTableStyle(selectedTemplate) === "horizontal") {
            horizontalRows.forEach(rowIdx => {
                questions.forEach((q) => {
                    const file = createMedia[`${q.id}_${rowIdx}`];
                    if (file) {
                        formData.append(`maker_media_${q.id}_${rowIdx}`, file);
                    }
                });
            });
        } else {
            questions.forEach((q) => {
                const file = createMedia[q.id];
                if (file) {
                    formData.append(`maker_media_${q.id}`, file);
                }
            });
        }

        setSignaturePurpose("create");
        setPendingCreateSubmitFormData(formData);
        setIsMakerSignatureModalOpen(true);

    };

    const handleCreateSubmitWithMakerSignature = async () => {
        if (!pendingCreateSubmitFormData) return;

        if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
            showToast("Please provide your signature.", "error");
            return;
        }

        const dataUrl = sigCanvasRef.current.toDataURL("image/png");
        const base64Part = dataUrl.split(",")[1] || "";

        const binary = atob(base64Part);
        const bytes = new Uint8Array(binary.length);

        for (let i = 0; i < binary.length; i += 1) {
            bytes[i] = binary.charCodeAt(i);
        }

        const signatureFile = new File(
            [bytes],
            `maker-create-signature-${Date.now()}.png`,
            { type: "image/png" }
        );

        pendingCreateSubmitFormData.append("maker_signature", signatureFile);

        setSignatureSubmitting(true);

        // Dynamic GPS capture and geofence evaluation at final submit action
        let userCoords = null;
        try {
            const geoCheck = await verifySitePresence({ projectId });
            if (!geoCheck.allowed) {
                setSignatureSubmitting(false);
                showToast(geoCheck.message || "Location verification failed.", "error");
                return;
            }
            if (!geoCheck.isBypassed && geoCheck.coords) {
                userCoords = geoCheck.coords;
            }
        } catch (geoErr) {
            setSignatureSubmitting(false);
            const userMsg =
                geoErr.userMessage ||
                geoErr.message ||
                "Location access is required to submit a Safety Inspection. Please enable GPS permissions and try again.";
            showToast(userMsg, "error");
            return;
        }

        if (userCoords) {
            pendingCreateSubmitFormData.append("user_latitude", String(formatCoordinate(userCoords.latitude, 7)));
            pendingCreateSubmitFormData.append("user_longitude", String(formatCoordinate(userCoords.longitude, 7)));
            if (userCoords.accuracy !== undefined && userCoords.accuracy !== null) {
                pendingCreateSubmitFormData.append("user_location_accuracy", String(userCoords.accuracy));
            }
        }

        try {
            await createAndSubmitSafetyChecklist(pendingCreateSubmitFormData);

            showToast("Checklist submitted to safety officers.", "success");

            setIsMakerSignatureModalOpen(false);
            setPendingCreateSubmitFormData(null);
            setSignaturePurpose(null);

            if (sigCanvasRef.current) sigCanvasRef.current.clear();

            backFromCreate();
            fetchList();
        } catch (err) {
            showToast(
                err?.response?.data?.detail || "Failed to submit checklist.",
                "error"
            );
        } finally {
            setSignatureSubmitting(false);
        }
    };

    const handleMakerSignatureSubmit = async () => {
        if (signaturePurpose === "create") {
            await handleCreateSubmitWithMakerSignature();
        } else {
            await handleSubmitWithMakerSignature();
        }
    };

    // ── render ───────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-slate-50/60 p-4 sm:p-6 lg:p-8">
            <div className={view === "dashboard" ? "mx-auto w-full max-w-[1600px] space-y-6" : "mx-auto w-full max-w-6xl 2xl:max-w-7xl"}>

                {!userId && (
                    <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                        User not found in session. Log in or ensure USER_DATA is set in localStorage.
                    </div>
                )}

                {loading && <div className="py-8 text-center text-muted-foreground">Loading…</div>}

                {!loading && error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>
                )}

                {!loading && !error && (
                    <>

                        {/* ═══ VIEW: CREATE CHECKLIST ═══ */}
                        {view === "create" && (
                            <section>
                                <button
                                    type="button"
                                    onClick={backFromCreate}
                                    className="mb-4 inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                >
                                    <ArrowLeft className="h-4 w-4" />
                                    Back to Dashboard
                                </button>

                                <div className="mb-6 rounded-xl border bg-card p-5 shadow-sm">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100">
                                            <ClipboardList className="h-5 w-5 text-orange-500" />
                                        </div>
                                        <div>
                                            <h1 className="text-lg font-bold text-foreground sm:text-xl">
                                                Start Safety Checklist
                                            </h1>
                                            <p className="text-sm text-muted-foreground">
                                                Choose the checklist to start the inspection.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-6 rounded-xl border bg-card p-5 shadow-sm">
                                    <label className="mb-1.5 block text-sm font-medium text-foreground">
                                        Checklists
                                    </label>

                                    <select
                                        value={selectedTemplateId}
                                        onChange={(e) => setSelectedTemplateId(e.target.value)}
                                        disabled={templatesLoading}
                                        className="h-11 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                                    >
                                        <option value="">
                                            {templatesLoading ? "Loading checklists..." : "Select checklist"}
                                        </option>

                                        {templates.map((tpl) => (
                                            <option key={tpl.id} value={tpl.id}>
                                                {tpl.title}
                                            </option>
                                        ))}
                                    </select>

                                    {!templatesLoading && templates.length === 0 && (
                                        <p className="mt-2 text-xs text-red-500">
                                            No active Safety Checklist formats found for this project.
                                        </p>
                                    )}
                                </div>

                                {templateLoading && (
                                    <div className="rounded-xl border bg-card p-5 text-sm text-muted-foreground">
                                        Loading questions...
                                    </div>
                                )}

                                {!templateLoading && selectedTemplate && (
                                    <>

                                        <ReportHeaderCreateCard
                                            selectedTemplate={selectedTemplate}
                                            reportMeta={reportMeta}
                                            setReportMeta={setReportMeta}
                                            projectId={projectId}
                                            projectConfigs={projectConfigs}
                                            currentUserProfile={currentUserProfile}
                                            isInternalMaker={isInternalMaker}
                                            contractors={contractors}
                                            contractorsLoading={contractorsLoading}
                                            projectName={projectName}
                                        />

                                        <div className="mb-4 rounded-xl border bg-card p-5 shadow-sm">
                                            <h2 className="text-base font-bold text-foreground">
                                                {selectedTemplate.title}
                                            </h2>
                                            <p className="mt-1 text-xs text-muted-foreground">
                                                {(selectedTemplate.questions || []).length} question(s)
                                            </p>
                                        </div>

                                        <InstructionBlock
                                            image={
                                                selectedTemplate?.instruction_image ||
                                                selectedTemplate?.instruction_image_url ||
                                                selectedTemplate?.template_instruction_image
                                            }
                                            text={
                                                selectedTemplate?.instruction_text ||
                                                selectedTemplate?.template_instruction_text
                                            }
                                            className="mb-4"
                                        />



                                        {/* <div className="mb-4 rounded-xl border bg-card p-5 shadow-sm">
                                            <h3 className="mb-4 text-sm font-semibold text-foreground">
                                                Inspection Details
                                            </h3>

                                            {isInternalMaker && (
                                                <div className="mb-4">
                                                    <p className="mt-1 text-xs text-gray-500">
                                                        If no contractor is selected, organization name will be used in the report.
                                                    </p>
                                                    <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                                                        Contractor Name
                                                    </label>

                                                    <select
                                                        value={reportMeta.name_of_contractor}
                                                        onChange={(e) =>
                                                            setReportMeta((prev) => ({
                                                                ...prev,
                                                                name_of_contractor: e.target.value,
                                                            }))
                                                        }
                                                        disabled={contractorsLoading}
                                                        className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                                                    >
                                                        <option value="">
                                                            {contractorsLoading
                                                                ? "Loading contractors..."
                                                                : "Use organization name"}
                                                        </option>

                                                        {contractors.map((contractor) => {
                                                            const name =
                                                                typeof contractor === "string"
                                                                    ? contractor
                                                                    : contractor?.name || contractor?.contractor_name || "";

                                                            if (!name) return null;

                                                            return (
                                                                <option key={name} value={name}>
                                                                    {name}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>

                                                    {!contractorsLoading && contractors.length === 0 && (
                                                        <p className="mt-1 text-xs text-red-500">
                                                            No contractor found in this project.
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                                <div>
                                                    <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                                                        Make / Model
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={reportMeta.make_model}
                                                        onChange={(e) =>
                                                            setReportMeta((prev) => ({
                                                                ...prev,
                                                                make_model: e.target.value,
                                                            }))
                                                        }
                                                        className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                                                        placeholder="Enter make / model"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                                                        Identification No.
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={reportMeta.identification_no}
                                                        onChange={(e) =>
                                                            setReportMeta((prev) => ({
                                                                ...prev,
                                                                identification_no: e.target.value,
                                                            }))
                                                        }
                                                        className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                                                        placeholder="Enter identification no."
                                                    />
                                                </div>

                                                <div>
                                                    <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                                                        Location
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={reportMeta.location}
                                                        onChange={(e) =>
                                                            setReportMeta((prev) => ({
                                                                ...prev,
                                                                location: e.target.value,
                                                            }))
                                                        }
                                                        className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                                                        placeholder="Enter location"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                                                        Name of Operator
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={reportMeta.name_of_operator}
                                                        onChange={(e) =>
                                                            setReportMeta((prev) => ({
                                                                ...prev,
                                                                name_of_operator: e.target.value,
                                                            }))
                                                        }
                                                        className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                                                        placeholder="Enter operator name"
                                                    />
                                                </div>
                                            </div>
                                        </div> */}

                                        <div className="space-y-4">
                                            {getTableStyle(selectedTemplate) === "horizontal" || getTableStyle(selectedTemplate) === "matrix" ? (
                                                <div className="space-y-4">
                                                    {horizontalRows.map((rowIdx, arrayIdx) => {
                                                        const isExpanded = expandedRows[rowIdx];
                                                        return (
                                                            <div key={rowIdx} className="rounded-xl border bg-card shadow-sm overflow-hidden">
                                                                <div
                                                                    className="flex items-center justify-between bg-muted/30 px-5 py-4 cursor-pointer hover:bg-muted/50 transition-colors"
                                                                    onClick={() => setExpandedRows(prev => ({ ...prev, [rowIdx]: !prev[rowIdx] }))}
                                                                >
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-100 text-orange-600 font-bold">
                                                                            {arrayIdx + 1}
                                                                        </div>
                                                                        <h3 className="font-bold text-foreground">
                                                                            {getTableStyle(selectedTemplate) === "matrix" 
                                                                                ? (selectedTemplate?.report_layout?.matrix_type === "MATRIX_WEEKLY" ? `Week ${arrayIdx + 1}` : `Day ${arrayIdx + 1}`) 
                                                                                : `Item ${arrayIdx + 1}`}
                                                                        </h3>
                                                                    </div>
                                                                    <div className="flex items-center gap-4">
                                                                        {horizontalRows.length > 1 && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    setHorizontalRows(prev => prev.filter(r => r !== rowIdx));
                                                                                }}
                                                                                className="text-red-500 hover:text-red-700 p-1"
                                                                                title="Remove Item"
                                                                            >
                                                                                <XIcon className="h-5 w-5" />
                                                                            </button>
                                                                        )}
                                                                        <div className="text-muted-foreground">
                                                                            {isExpanded ? (
                                                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>
                                                                            ) : (
                                                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {isExpanded && (
                                                                    <div className="p-5 border-t space-y-4 bg-gray-50/30">
                                                                        {(selectedTemplate.questions || []).map((q, idx) => {
                                                                            const keyId = `${q.id}_${rowIdx}`;
                                                                            return (
                                                                                <div key={keyId} className="rounded-xl border bg-white p-5 shadow-sm">
                                                                                    <div className="mb-4 flex items-start gap-3">
                                                                                        <QuestionBadge number={idx + 1} />
                                                                                        <div>
                                                                                            <p className="pt-1 text-sm font-semibold text-foreground sm:text-base">
                                                                                                {q.text}
                                                                                            </p>
                                                                                            {q.description && (
                                                                                                <p className="mt-1 text-xs text-muted-foreground">
                                                                                                    {q.description}
                                                                                                </p>
                                                                                            )}
                                                                                            {q.reference_image_url && arrayIdx === 0 && (
                                                                                                <div className="mt-3 mb-1">
                                                                                                    <img
                                                                                                        src={resolveMediaUrl(q.reference_image_url)}
                                                                                                        alt="Reference"
                                                                                                        className="max-h-24 object-contain rounded border shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
                                                                                                        onClick={() => window.open(resolveMediaUrl(q.reference_image_url), "_blank")}
                                                                                                    />
                                                                                                </div>
                                                                                            )}
                                                                                            {/* Quantity (Read-only) */}
                                                                                            {(q.quantity_value || q.quantity_unit) && (
                                                                                                <div className="mt-2 flex items-center gap-2 text-sm text-gray-700">
                                                                                                    <span className="font-semibold">Quantity:</span> {q.quantity_value || "-"} {q.quantity_unit || ""}
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>

                                                                                    <div className="space-y-3">
                                                                                        {(!q.type || q.type === "multiple_choice" || (q.type !== "short_answer" && q.type !== "paragraph" && q.type !== "date")) ? (
                                                                                            <TemplateOptionButtons
                                                                                                options={q.options}
                                                                                                value={createAnswers[keyId]}
                                                                                                onChange={(value) =>
                                                                                                    setCreateAnswers((prev) => ({ ...prev, [keyId]: value }))
                                                                                                }
                                                                                            />
                                                                                        ) : q.type === "short_answer" ? (
                                                                                            <input
                                                                                                type="text"
                                                                                                value={createAnswers[keyId] || ""}
                                                                                                onChange={(e) =>
                                                                                                    setCreateAnswers((prev) => ({ ...prev, [keyId]: e.target.value }))
                                                                                                }
                                                                                                className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-foreground focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100"
                                                                                                placeholder="Enter your answer..."
                                                                                            />
                                                                                        ) : q.type === "date" ? (
                                                                                            <input
                                                                                                type="date"
                                                                                                disabled={Array.isArray(q.options) && q.options.includes("autoFetchOneMonth")}
                                                                                                value={displayToIsoDate(createAnswers[keyId]) || ""}
                                                                                                onChange={(e) =>
                                                                                                    setCreateAnswers((prev) => ({ ...prev, [keyId]: e.target.value }))
                                                                                                }
                                                                                                className="w-full max-w-sm rounded-lg border border-border bg-white px-3 py-2 text-sm text-foreground focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100 disabled:opacity-70 disabled:bg-gray-100"
                                                                                            />
                                                                                        ) : (
                                                                                            <textarea
                                                                                                rows={3}
                                                                                                value={createAnswers[keyId] || ""}
                                                                                                onChange={(e) =>
                                                                                                    setCreateAnswers((prev) => ({ ...prev, [keyId]: e.target.value }))
                                                                                                }
                                                                                                className="w-full resize-y rounded-lg border border-border bg-white px-3 py-2 text-sm text-foreground focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100"
                                                                                                placeholder="Enter your answer..."
                                                                                            />
                                                                                        )}

                                                                                        <div>
                                                                                            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                                                                                                Remark / Comment
                                                                                            </label>
                                                                                            <textarea
                                                                                                rows={1}
                                                                                                value={createRemarks[keyId] || ""}
                                                                                                onChange={(e) => {
                                                                                                    setCreateRemarks((prev) => ({ ...prev, [keyId]: e.target.value }));
                                                                                                    autoResizeTextarea(e.target);
                                                                                                }}
                                                                                                className="w-full resize-y rounded-lg border border-border bg-white px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100"
                                                                                                placeholder="Add a remark if any..."
                                                                                            />
                                                                                        </div>

                                                                                        <div className="pt-2">
                                                                                            <PhotoUploadArea
                                                                                                id={`create-media-${keyId}`}
                                                                                                previewBase64={createMedia[keyId]}
                                                                                                label={q.photo_required ? "Photo Evidence (Required)" : "Photo Evidence"}
                                                                                                onFileChange={(e) => {
                                                                                                    const file = e.target.files?.[0];
                                                                                                    if (!file) return;
                                                                                                    setCreateMedia((prev) => ({ ...prev, [keyId]: file }));
                                                                                                }}
                                                                                                onRemove={() =>
                                                                                                    setCreateMedia((prev) => ({ ...prev, [keyId]: undefined }))
                                                                                                }
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}

                                                    <div className="flex justify-end pt-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const newRowIdx = Math.max(...horizontalRows, 0) + 1;
                                                                setHorizontalRows(prev => [...prev, newRowIdx]);
                                                                setExpandedRows(prev => ({ ...prev, [newRowIdx]: true }));

                                                                setCreateAnswers(prev => {
                                                                    const next = { ...prev };
                                                                    (selectedTemplate?.questions || []).forEach((q) => {
                                                                        const keyId = `${q.id}_${newRowIdx}`;
                                                                        if (q.type === "date" && Array.isArray(q.options) && q.options.includes("autoFetchOneMonth")) {
                                                                            const d = new Date();
                                                                            d.setMonth(d.getMonth() + 1);
                                                                            next[keyId] = d.toISOString().split("T")[0];
                                                                        } else {
                                                                            next[keyId] = "";
                                                                        }
                                                                    });
                                                                    return next;
                                                                });
                                                                setCreateRemarks(prev => {
                                                                    const next = { ...prev };
                                                                    (selectedTemplate?.questions || []).forEach((q) => {
                                                                        const keyId = `${q.id}_${newRowIdx}`;
                                                                        next[keyId] = "";
                                                                    });
                                                                    return next;
                                                                });
                                                            }}
                                                            className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:shadow-lg transition-all"
                                                        >
                                                            <Plus className="h-4 w-4" /> Add Item
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : buildSectionedQuestionRows({
                                                    questions: selectedTemplate.questions || [],
                                                    sectionConfig: selectedTemplate?.report_layout?.section_config,
                                                    getOrderIndex: (q, idx) => idx + 1,
                                                }).map((entry, idx) => {
                                                    if (entry.type === "section") {
                                                        return (
                                                            <SafetyChecklistSectionHeader
                                                                key={`sec-${entry.key || idx}`}
                                                                label={entry.label}
                                                            />
                                                        );
                                                    }
                                                    const q = entry.question;
                                                    return (
                                                        <div
                                                            key={q.id}
                                                            className="rounded-xl border bg-card p-5 shadow-sm transition-shadow hover:shadow-md"
                                                        >
                                                            <div className="mb-4 flex items-start gap-3">
                                                                <QuestionBadge number={entry.serial_no} />
                                                                <div>
                                                                    <p className="pt-1 text-sm font-semibold text-foreground sm:text-base">
                                                                        {q.text}
                                                                    </p>

                                                                    {q.description && (
                                                                        <p className="mt-1 text-xs text-muted-foreground">
                                                                            {q.description}
                                                                        </p>
                                                                    )}
                                                                    {q.reference_image_url && (
                                                                        <div className="mt-3 mb-1">
                                                                            <img
                                                                                src={resolveMediaUrl(q.reference_image_url)}
                                                                                alt="Reference"
                                                                                className="max-h-24 object-contain rounded border shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
                                                                                onClick={() => window.open(resolveMediaUrl(q.reference_image_url), "_blank")}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    {/* Quantity (Read-only) */}
                                                                    {(q.quantity_value || q.quantity_unit) && (
                                                                        <div className="mt-2 flex items-center gap-2 text-sm text-gray-700">
                                                                            <span className="font-semibold">Quantity:</span> {q.quantity_value || "-"} {q.quantity_unit || ""}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="space-y-3">
                                                                {(!q.type || q.type === "multiple_choice" || (q.type !== "short_answer" && q.type !== "paragraph" && q.type !== "date")) ? (
                                                                    <TemplateOptionButtons
                                                                        options={q.options}
                                                                        value={createAnswers[q.id]}
                                                                        onChange={(value) =>
                                                                            setCreateAnswers((prev) => ({
                                                                                ...prev,
                                                                                [q.id]: value,
                                                                            }))
                                                                        }
                                                                    />
                                                                ) : q.type === "short_answer" ? (
                                                                    <input
                                                                        type="text"
                                                                        value={createAnswers[q.id] || ""}
                                                                        onChange={(e) =>
                                                                            setCreateAnswers((prev) => ({
                                                                                ...prev,
                                                                                [q.id]: e.target.value,
                                                                            }))
                                                                        }
                                                                        className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-foreground focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100"
                                                                        placeholder="Enter your answer..."
                                                                    />
                                                                ) : q.type === "date" ? (
                                                                    <input
                                                                        type="date"
                                                                        disabled={Array.isArray(q.options) && q.options.includes("autoFetchOneMonth")}
                                                                        value={displayToIsoDate(createAnswers[q.id]) || ""}
                                                                        onChange={(e) =>
                                                                            setCreateAnswers((prev) => ({
                                                                                ...prev,
                                                                                [q.id]: e.target.value,
                                                                            }))
                                                                        }
                                                                        className="w-full max-w-sm rounded-lg border border-border bg-white px-3 py-2 text-sm text-foreground focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100 disabled:opacity-70 disabled:bg-gray-100"
                                                                    />
                                                                ) : (
                                                                    <textarea
                                                                        rows={3}
                                                                        value={createAnswers[q.id] || ""}
                                                                        onChange={(e) =>
                                                                            setCreateAnswers((prev) => ({
                                                                                ...prev,
                                                                                [q.id]: e.target.value,
                                                                            }))
                                                                        }
                                                                        className="w-full resize-y rounded-lg border border-border bg-white px-3 py-2 text-sm text-foreground focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100"
                                                                        placeholder="Enter your answer..."
                                                                    />
                                                                )}

                                                                <div>
                                                                    <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                                                                        Remark / Comment
                                                                    </label>
                                                                    <textarea
                                                                        rows={1}
                                                                        value={createRemarks[q.id] || ""}
                                                                        onChange={(e) => {
                                                                            setCreateRemarks((prev) => ({
                                                                                ...prev,
                                                                                [q.id]: e.target.value,
                                                                            }));

                                                                            autoResizeTextarea(e.target);
                                                                        }}
                                                                        onInput={(e) => autoResizeTextarea(e.target)}
                                                                        className="min-h-[42px] w-full resize-none overflow-hidden rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
                                                                        placeholder="Add a comment/remark if any..."
                                                                    />
                                                                </div>

                                                                <PhotoUploadArea
                                                                    id={`create-maker-${q.id}`}
                                                                    previewBase64={createMedia[q.id]}
                                                                    label={
                                                                        q.photo_required
                                                                            ? "Upload photo (required)"
                                                                            : "Upload photo (optional)"
                                                                    }
                                                                    onFileChange={(e) => {
                                                                        const file = e.target.files?.[0];
                                                                        if (!file) return;

                                                                        setCreateMedia((prev) => ({
                                                                            ...prev,
                                                                            [q.id]: file,
                                                                        }));
                                                                    }}
                                                                    onRemove={() =>
                                                                        setCreateMedia((prev) => ({
                                                                            ...prev,
                                                                            [q.id]: undefined,
                                                                        }))
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                        </div>

                                        <div className="mt-6">
                                            <button
                                                type="button"
                                                onClick={handleCreateAndSubmitChecklist}
                                                disabled={submitting}
                                                className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-3.5 text-sm font-bold text-white shadow-lg shadow-orange-200 transition-all hover:shadow-xl hover:shadow-orange-300 disabled:opacity-60"
                                            >
                                                <Send className="h-4 w-4" />
                                                {submitting ? "Submitting..." : "Submit Checklist"}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </section>
                        )}

                        {/* ═══ VIEW: MAKER FIX ═══ */}
                        {view === "maker_fix" && detail && (
                            <section>
                                <button type="button" onClick={backToDashboard}
                                    className="mb-4 inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                                    <ArrowLeft className="h-4 w-4" /> Back to Dashboard
                                </button>

                                <div className="mb-6 rounded-xl border bg-card p-5 shadow-sm">
                                    <h1 className="text-lg font-bold text-foreground sm:text-xl">{detail.name}</h1>
                                    <p className="mt-1 text-sm text-muted-foreground">
                                        {makerEditableItems.length} item(s) to fix
                                    </p>
                                </div>

                                <ReportHeaderInfoCard detail={detail} />

                                <InstructionBlock
                                    image={
                                        detail?.template_instruction_image ||
                                        detail?.instruction_image ||
                                        detail?.instruction_image_url
                                    }
                                    text={
                                        detail?.template_instruction_text ||
                                        detail?.instruction_text
                                    }
                                    className="mb-4"
                                />

                                {makerEditableItems.length === 0 ? (
                                    <p className="py-8 text-center text-sm text-muted-foreground">No items pending for you right now.</p>
                                ) : (
                                    <div className="space-y-4">
                                        {renderFixItemList(makerFixItems, ({ item, sub, editable, highlighted }, idx, displayIndex) => {
                                            const checkerComment =
                                                sub.latest_checker_reject_remarks ||
                                                sub.latest_checker_remarks ||
                                                sub.checker_remarks ||
                                                "";

                                            const checkerPhoto =
                                                sub.latest_checker_reject_photo_url ||
                                                sub.latest_checker_photo_url ||
                                                "";

                                            const inputType = item.input_type || item.type || "multiple_choice";

                                            return (
                                                <div
                                                    key={item.id}
                                                    className={`rounded-xl border p-5 shadow-sm transition-shadow hover:shadow-md ${highlighted
                                                        ? "border-red-300 bg-red-50"
                                                        : "bg-card border-border opacity-75"
                                                        }`}
                                                >

                                                    {highlighted ? (
                                                        <span className="inline-flex rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
                                                            Rework Required
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">
                                                            No Rectification required
                                                        </span>
                                                    )}

                                                    <div className="mb-4 flex items-start gap-3">
                                                        <QuestionBadge number={displayIndex ?? idx + 1} />
                                                        <p className="pt-1 text-sm font-semibold text-foreground sm:text-base">
                                                            {item.title}
                                                        </p>
                                                    </div>

                                                    <div className="space-y-3">
                                                        {sub.supervisor_remarks && (
                                                            <RemarksBubble label="Supervisor" text={sub.supervisor_remarks} />
                                                        )}
                                                        {checkerPhoto && (
                                                            <PhotoViewButton
                                                                url={checkerPhoto}
                                                                label="View Checker Photo"
                                                            />
                                                        )}

                                                        {inputType === "multiple_choice" || (inputType !== "short_answer" && inputType !== "paragraph" && inputType !== "date") ? (
                                                            <TemplateOptionButtons
                                                                options={item.options}
                                                                value={makerAnswers[sub.id]}
                                                                onChange={(value) => {
                                                                    if (!editable) return;
                                                                    setMakerAnswers((prev) => ({
                                                                        ...prev,
                                                                        [sub.id]: value,
                                                                    }));
                                                                }}
                                                            />
                                                        ) : inputType === "short_answer" ? (
                                                            <input
                                                                type="text"
                                                                disabled={!editable}
                                                                value={makerAnswers[sub.id] || ""}
                                                                onChange={(e) => {
                                                                    if (!editable) return;
                                                                    setMakerAnswers((prev) => ({
                                                                        ...prev,
                                                                        [sub.id]: e.target.value,
                                                                    }));
                                                                }}
                                                                className={`w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-foreground focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100 ${!editable ? "cursor-not-allowed opacity-60 bg-muted/30" : ""}`}
                                                                placeholder="Enter your answer..."
                                                            />
                                                        ) : inputType === "date" ? (
                                                            <input
                                                                type="date"
                                                                disabled={!editable}
                                                                value={displayToIsoDate(makerAnswers[sub.id]) || ""}
                                                                onChange={(e) => {
                                                                    if (!editable) return;
                                                                    setMakerAnswers((prev) => ({
                                                                        ...prev,
                                                                        [sub.id]: e.target.value,
                                                                    }));
                                                                }}
                                                                className={`w-full max-w-sm rounded-lg border border-border bg-white px-3 py-2 text-sm text-foreground focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100 ${!editable ? "cursor-not-allowed opacity-60 bg-muted/30" : ""}`}
                                                            />
                                                        ) : (
                                                            <textarea
                                                                rows={3}
                                                                disabled={!editable}
                                                                value={makerAnswers[sub.id] || ""}
                                                                onChange={(e) => {
                                                                    if (!editable) return;
                                                                    setMakerAnswers((prev) => ({
                                                                        ...prev,
                                                                        [sub.id]: e.target.value,
                                                                    }));
                                                                }}
                                                                className={`w-full resize-y rounded-lg border border-border bg-white px-3 py-2 text-sm text-foreground focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100 ${!editable ? "cursor-not-allowed opacity-60 bg-muted/30" : ""}`}
                                                                placeholder="Enter your answer..."
                                                            />
                                                        )}

                                                        {checkerComment && (
                                                            <RemarksBubble
                                                                label="Checker Comment"
                                                                text={checkerComment}
                                                            />
                                                        )}

                                                        <div>
                                                            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                                                                Remarks (what was fixed / checked)
                                                            </label>
                                                            <textarea
                                                                disabled={!editable}
                                                                className={`w-full rounded-lg border border-border bg-muted/30 p-3 text-sm ${!editable ? "cursor-not-allowed opacity-60" : ""
                                                                    }`}
                                                                value={makerRemarks[sub.id] ?? ""}
                                                                onChange={(e) => {
                                                                    if (!editable) return;

                                                                    setMakerRemarks((prev) => ({
                                                                        ...prev,
                                                                        [sub.id]: e.target.value,
                                                                    }));
                                                                }}
                                                                rows={2}
                                                            />
                                                        </div>

                                                        {editable && (
                                                            <PhotoUploadArea
                                                                id={`maker-fix-${sub.id}`}
                                                                previewBase64={makerMedia[sub.id]}
                                                                label={
                                                                    item.photo_required
                                                                        ? "Upload rework photo (required)"
                                                                        : "Upload rework photo (optional)"
                                                                }
                                                                onFileChange={(e) => {
                                                                    const file = e.target.files?.[0];
                                                                    if (!file) return;

                                                                    setMakerMedia((prev) => ({
                                                                        ...prev,
                                                                        [sub.id]: file,
                                                                    }));
                                                                }}
                                                                onRemove={() =>
                                                                    setMakerMedia((prev) => ({
                                                                        ...prev,
                                                                        [sub.id]: undefined,
                                                                    }))
                                                                }
                                                            />
                                                        )}

                                                        {/* <div className="flex justify-end pt-1">
                                                            <button type="button"
                                                                onClick={() => handleSubmitSingleFix(sub.id)}
                                                                disabled={submitting}
                                                                className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-primary to-primary/90 px-4 py-2 text-xs font-semibold text-primary-foreground shadow-sm transition-all hover:shadow-md disabled:opacity-60">
                                                                <Send className="h-3.5 w-3.5" />
                                                                Submit Fix
                                                            </button>
                                                        </div> */}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}

                                <SafetyChecklistSignOffSection
                                    detail={detail}
                                    className="mt-6"
                                />

                                {makerEditableItems.length > 0 && (
                                    <div className="mt-6">
                                        <button type="button" onClick={handleSubmitAllFixes} disabled={submitting}
                                            className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-3.5 text-sm font-bold text-white shadow-lg shadow-orange-200 transition-all hover:shadow-xl hover:shadow-orange-300 disabled:opacity-60">
                                            <Send className="h-4 w-4" />
                                            {submitting ? "Submitting…" : "Submit All Fixes"}
                                        </button>
                                    </div>
                                )}
                            </section>
                        )}

                        {/* ═══ VIEW: DASHBOARD ═══ */}
                        {view === "dashboard" && (
                            <section>
                                {/* Header Banner */}
                                <div className="mb-6">
                                    <SafetyHeroBanner
                                        type="checklist"
                                        categoryTag="SAFETY AUDITS & AUDIT TRAIL"
                                        categoryIcon={ShieldCheck}
                                        title="Safety Checklist Dashboard"
                                        subtitle="Safety inspection & checklist management — initiate checks, monitor progress, and log resolutions."
                                    >
                                        <button
                                            type="button"
                                            onClick={openCreateChecklist}
                                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-2.5 text-xs sm:text-sm font-semibold text-white shadow-sm transition-all hover:shadow-md hover:from-orange-600 hover:to-orange-700 cursor-pointer"
                                        >
                                            <Plus className="h-4 w-4" />
                                            Start Checklist
                                        </button>
                                    </SafetyHeroBanner>
                                </div>

                                {/* Counter cards */}
                                <div className="mb-6 grid grid-cols-2 gap-3.5 sm:grid-cols-3 lg:grid-cols-5">
                                    {[
                                        {
                                            key: "pending_review",
                                            label: "Pending Reviews",
                                            icon: <Clock className="h-3.5 w-3.5" />,
                                            colorCls: "hsl(45, 93%, 47%)",
                                            count: makerPendingReview.length,
                                        },
                                        {
                                            key: "pending_rectification",
                                            label: "Pending Rectifications",
                                            icon: <CircleAlert className="h-3.5 w-3.5" />,
                                            colorCls: "hsl(24.6, 95%, 53.1%)",
                                            count: makerPendingRectification.length,
                                        },
                                        {
                                            key: "approved",
                                            label: "Approved",
                                            icon: <CheckCircle className="h-3.5 w-3.5" />,
                                            colorCls: "hsl(145, 65%, 42%)",
                                            count: makerApprovedCount,
                                        },
                                        {
                                            key: "rejected",
                                            label: "Rejected",
                                            icon: <CircleAlert className="h-3.5 w-3.5" />,
                                            colorCls: "hsl(0, 75%, 55%)",
                                            count: makerRejectedCount,
                                        },
                                        {
                                            key: "total",
                                            label: "Total",
                                            icon: <ClipboardList className="h-3.5 w-3.5" />,
                                            colorCls: "hsl(263, 83%, 53%)",
                                            count: makerTotalCount,
                                        },
                                    ].map(({ key, label, icon, colorCls, count }) => (
                                        <div
                                            key={key || label}
                                            className="group relative flex flex-col justify-between h-full rounded-2xl border border-slate-200/90 bg-card p-3.5 sm:p-4 shadow-2xs transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-slate-300 select-none"
                                        >
                                            {/* Top Row: Icon Badge + Label with fixed min-height for uniform alignment */}
                                            <div className="flex items-start gap-2 min-h-[38px]">
                                                <span
                                                    className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg transition-colors"
                                                    style={{
                                                        backgroundColor: `${colorCls}18`,
                                                        color: colorCls,
                                                    }}
                                                >
                                                    {icon}
                                                </span>
                                                <span
                                                    className="text-xs sm:text-[13px] font-semibold leading-snug line-clamp-2"
                                                    style={{ color: colorCls }}
                                                >
                                                    {label}
                                                </span>
                                            </div>

                                            {/* Bottom Row: Count strictly aligned to horizontal baseline */}
                                            <div className="mt-3 flex items-baseline border-t border-border/40 pt-2.5">
                                                <span className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
                                                    {Number.isFinite(Number(count)) ? Number(count) : 0}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Active Queues: Pending for Review & Pending for Rectification */}
                                <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Pending for Review */}
                                    <div className="flex flex-col">
                                        <div className="mb-2.5 flex items-center gap-2">
                                            <Clock className="h-4 w-4 text-yellow-500" />
                                            <h2 className="text-sm font-semibold text-yellow-500 sm:text-base">
                                                Pending for Review
                                            </h2>
                                            <span className="rounded-full bg-yellow-100/80 px-2 py-0.5 text-xs font-semibold text-yellow-800">
                                                {makerPendingReview.length}
                                            </span>
                                        </div>

                                        <div className="flex-1 rounded-xl border border-slate-200/90 bg-card shadow-2xs overflow-hidden">
                                            {makerPendingReview.length === 0 ? (
                                                <p className="p-6 text-sm text-center text-muted-foreground">No items pending review</p>
                                            ) : (
                                                <DashboardBucketList
                                                    count={makerPendingReview.length}
                                                    ariaLabel="Pending for Review checklists"
                                                >
                                                    {makerPendingReview.map((item) => (
                                                        <div
                                                            key={item.id}
                                                            className="flex flex-col gap-2 border-b border-border/50 p-4 last:border-0 sm:flex-row sm:items-center sm:justify-between transition-colors hover:bg-slate-50/50"
                                                        >
                                                            <div className="min-w-0 flex-1">
                                                                <p className="break-words font-medium text-foreground">{item.name}</p>
                                                                <p className="text-xs text-muted-foreground">
                                                                    {formatChecklistDateTime(item.created_at)}
                                                                    {totalQuestionsLabel(item) ? ` · ${totalQuestionsLabel(item)}` : ""}
                                                                </p>
                                                                <ChecklistListMeta item={item} />
                                                            </div>

                                                            <div className="flex shrink-0 items-center gap-2">
                                                                <span className="inline-flex items-center rounded-full bg-yellow-100 px-3 py-1 text-xs font-semibold text-orange-700">
                                                                    In Review
                                                                </span>

                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setReadonlyChecklist(item);
                                                                        setReadonlyPendingStatusLabel("In Review");
                                                                    }}
                                                                    className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition-colors"
                                                                >
                                                                    View
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </DashboardBucketList>
                                            )}
                                        </div>
                                    </div>

                                    {/* Pending for Rectification */}
                                    <div className="flex flex-col">
                                        <div className="mb-2.5 flex items-center gap-2">
                                            <Send className="h-4 w-4 text-orange-500" />
                                            <h2 className="text-sm font-semibold text-orange-500 sm:text-base">
                                                Pending for Rectification
                                            </h2>
                                            <span className="rounded-full bg-orange-100/80 px-2 py-0.5 text-xs font-semibold text-orange-800">
                                                {makerPendingRectification.length}
                                            </span>
                                        </div>

                                        <div className="flex-1 rounded-xl border border-slate-200/90 bg-card shadow-2xs overflow-hidden">
                                            {makerPendingRectification.length === 0 ? (
                                                <p className="p-6 text-sm text-center text-muted-foreground">No items pending rectification</p>
                                            ) : (
                                                <DashboardBucketList
                                                    count={makerPendingRectification.length}
                                                    ariaLabel="Pending for Rectification checklists"
                                                >
                                                    {makerPendingRectification.map((item) => {
                                                        const canRectify = makerCanRectify(item);

                                                        return (
                                                            <div
                                                                key={item.id}
                                                                className="flex flex-col gap-2 border-b border-border/50 p-4 last:border-0 sm:flex-row sm:items-center sm:justify-between transition-colors hover:bg-slate-50/50"
                                                            >
                                                                <div className="min-w-0 flex-1">
                                                                    <p className="break-words font-medium text-foreground">{item.name}</p>
                                                                    <p className="text-xs text-muted-foreground">
                                                                        {item.created_at ? formatChecklistDateTime(item.created_at) : ""}
                                                                        {rejectedPointsLabel(item) ? ` · ${rejectedPointsLabel(item)}` : ""}
                                                                    </p>
                                                                    <ChecklistListMeta item={item} />
                                                                </div>

                                                                <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                                                                    <span
                                                                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${canRectify
                                                                            ? "bg-red-100 text-orange-500"
                                                                            : "bg-orange-100 text-orange-500"
                                                                            }`}
                                                                    >
                                                                        {canRectify ? "Rectification Pending" : "Rectifying"}
                                                                    </span>

                                                                    {canRectify ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => openMakerFix(item)}
                                                                            className="rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-all hover:shadow-md"
                                                                        >
                                                                            Rectify now
                                                                        </button>
                                                                    ) : (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setReadonlyChecklist(item);
                                                                                setReadonlyPendingStatusLabel("Pending for Rectification");
                                                                            }}
                                                                            className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition-colors"
                                                                        >
                                                                            View
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </DashboardBucketList>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <ChecklistFilters
                                    filters={filters}
                                    setFilters={setFilters}
                                    contractorOptions={contractorOptions}
                                    typeOptions={typeOptions}
                                />

                                {/* Completed */}
                                <div className="mb-6">
                                    <div className="mb-2.5 flex items-center gap-2">
                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                        <h2 className="text-sm font-semibold text-green-600 sm:text-base">Completed Checklists</h2>
                                        <span className="rounded-full bg-green-100/80 px-2 py-0.5 text-xs font-semibold text-green-800">{makerCompletedChecklists.length}</span>
                                    </div>
                                    <div className="rounded-xl border border-slate-200/90 bg-card shadow-2xs overflow-hidden">
                                        {makerCompletedChecklists.length === 0 ? (
                                            <p className="p-6 text-sm text-center text-muted-foreground">No completed items</p>
                                        ) : (
                                            <DashboardBucketList
                                                count={makerCompletedChecklists.length}
                                                ariaLabel="Completed checklists"
                                            >
                                                {makerCompletedChecklists.map((item) => (
                                                    <div
                                                        key={item.id}
                                                        className="flex flex-col gap-2 border-b border-border/50 p-4 last:border-0 sm:flex-row sm:items-center sm:justify-between transition-colors hover:bg-slate-50/50"
                                                    >
                                                        <div className="min-w-0 flex-1">
                                                            <p className="break-words font-medium text-foreground">{item.name}</p>
                                                            <p className="text-xs text-muted-foreground">
                                                                {item.created_at ? formatChecklistDateTime(item.created_at) : ""}
                                                                {totalQuestionsLabel(item) ? ` · ${totalQuestionsLabel(item)}` : ""}
                                                            </p>
                                                            <ChecklistListMeta item={item} />
                                                        </div>

                                                        <div className="flex shrink-0 flex-wrap items-center gap-3 sm:justify-end">
                                                            <span
                                                                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${isClosedRejected(item)
                                                                    ? "bg-red-100 text-red-700"
                                                                    : "bg-green-100 text-green-700"
                                                                    }`}
                                                            >
                                                                {isClosedRejected(item) ? "Rejected" : "Approved"}
                                                            </span>

                                                            <button
                                                                type="button"
                                                                title="View History"
                                                                onClick={() => setHistoryChecklist(item)}
                                                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 transition-colors hover:bg-blue-100"
                                                            >
                                                                <Eye className="h-4 w-4" />
                                                            </button>

                                                            <button
                                                                type="button"
                                                                title="Download Report"
                                                                onClick={() => handleDownloadReport(item)}
                                                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-green-200 bg-green-50 text-green-700 transition-colors hover:bg-green-100"
                                                            >
                                                                <Download className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </DashboardBucketList>
                                        )}
                                    </div>
                                </div>
                            </section>
                        )}
                    </>
                )}

                <SafetyChecklistReadonlyModal
                    open={!!readonlyChecklist}
                    checklistId={readonlyChecklist?.id}
                    pendingStatusLabel={readonlyPendingStatusLabel}
                    onClose={() => {
                        setReadonlyChecklist(null);
                        setReadonlyPendingStatusLabel("");
                    }}
                />

                <SafetyChecklistHistoryModal
                    open={!!historyChecklist}
                    checklistId={historyChecklist?.id}
                    title={historyChecklist?.name}
                    onClose={() => setHistoryChecklist(null)}
                />

                {isMakerSignatureModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                        <div className="w-full max-w-2xl rounded-xl bg-white p-5 shadow-xl">
                            <div className="mb-3 flex items-center justify-between">
                                <h3 className="text-base font-semibold text-foreground">
                                    Final Signature
                                </h3>
                                <button
                                    type="button"
                                    onClick={handleCloseMakerSignatureModal}
                                    className="rounded p-1 text-muted-foreground hover:bg-muted"
                                >
                                    <XIcon className="h-4 w-4" />
                                </button>
                            </div>
                            <p className="mb-3 text-xs text-muted-foreground">
                                Draw your signature and submit to complete the checklist action.
                            </p>
                            <div className="rounded-lg border border-border bg-white p-2">
                                <SafetyResponsiveSignatureCanvas
                                    ref={sigCanvasRef}
                                    penColor="black"
                                />
                            </div>
                            <div className="mt-3 flex flex-col gap-2">
                                <button
                                    type="button"
                                    onClick={() => sigCanvasRef.current?.clear()}
                                    className="w-full rounded-lg border border-slate-200 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                                >
                                    Clear
                                </button>
                                <button
                                    type="button"
                                    onClick={handleMakerSignatureSubmit}
                                    disabled={signatureSubmitting}
                                    className="w-full rounded-lg bg-gradient-to-r from-orange-400 to-orange-500 py-2.5 text-xs font-semibold text-white shadow-sm hover:from-orange-500 hover:to-orange-600 disabled:opacity-60 transition-all"
                                >
                                    {signatureSubmitting
                                        ? "Submitting..."
                                        : signaturePurpose === "create"
                                            ? "Submit Checklist"
                                            : "Submit Rectification"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
